/**
 * 工具翻译分区（i18n 命名空间思路，2026-08-13）
 *
 * 独立文件维护：AI 工具名/状态文案改动只改这里，不碰 5369 行主字典。
 * 未来其他分区（世界积木/商城等）同样模式加文件，translations.ts 运行时合并。
 *
 * key 结构：
 *   toolName.{name}    → 工具名（file_read → 读取文件）
 *   tool.{status}      → 状态模板（running/update/done，{name}/{summary} 插值）
 */

import type { TranslationDict } from '../types'

export const toolZh: TranslationDict = {
  // 总商城（世界商城 / 插件商城 / 已安装）
  'store.sectionPlugins': '插件商城',
  'store.sectionInstalled': '已安装',
  'store.sectionSource': '商城源',
  'store.needAdmin': '需要管理员权限',
  'store.searchPackages': '搜索插件包',
  'store.packageCount': '共 {n} 个安装包',
  'store.uploadPackage': '上传安装包',
  'store.uploadSuccess': '安装包已入库，确认 manifest 后再安装',
  'store.refresh': '刷新',
  'store.loading': '加载中…',
  'store.empty': '还没有安装包',
  'store.emptyHint': '管理员上传插件安装包后，会出现在这里',
  'store.emptyHintAdmin': '上传一个 zip（根目录含 plugin.json）即可开始',
  'store.readonlyHint': '安装、更新与卸载需要管理员权限',
  'store.detail': '详情',
  'store.close': '关闭',
  'store.install': '安装',
  'store.upgrade': '更新',
  'store.uninstall': '卸载',
  'store.installed': '已安装',
  'store.notInstalled': '未安装',
  'store.updatable': '可更新',
  'store.builtinConflict': '与内置插件同名，不可安装',
  'store.brokenPackage': '安装包损坏',
  'store.noDescription': '（无说明）',
  'store.uncollected': '未收录',
  'store.deletePackage': '删除安装包',
  'store.deleteSuccess': '安装包已删除',
  'store.installSuccess': '已安装「{name}」',
  'store.upgradeSuccess': '已更新「{name}」',
  'store.uninstallSuccess': '已卸载「{name}」',
  'store.confirmUninstall': '卸载「{name}」？该插件的配置会一并删除，内置插件不受影响。',
  'store.confirmDeletePackage': '删除安装包「{name}」？已安装的插件不受影响。',
  'store.fileCount': '{n} 个文件',
  'store.fieldId': '插件 id',
  'store.fieldVersion': '版本',
  'store.fieldAuthor': '作者',
  'store.fieldCategory': '类型',
  'store.fieldEntry': '载荷文件',
  'store.fieldSize': '解压后',
  'store.fieldSha': 'SHA-256',
  'store.fieldFiles': '包含文件',
  'store.catSkin': '皮肤',
  'store.catSkill': '技能',
  'store.catWorld': '世界',
  'store.catService': '服务',
  'store.catBuiltin': '内置能力',
  'store.catEmojiPack': '表情包',
  'store.catOther': '其他',
  // 我的 AI · 通道（QQ）：配置、配对、隐私说明
  'channel.title': 'QQ 通道',
  'channel.desc': '让你的 AI 在 QQ 上接客：群里被 @、或私聊机器人都会进 Copree，回复发回 QQ',
  'channel.modalTitle': 'AI 通道',
  'channel.hostedTitle': '协议端登录',
  'channel.hostedScan': '用手机 QQ 扫描二维码，授权给这个协议端（首次必须本人在手机上确认；码会自动轮换，过期不用管）',
  'channel.hostedWaiting': '正在等协议端生成二维码…',
  'channel.hostedLoggedIn': '协议端已登录：{name}',
  'channel.hostedLinkHint': '在电脑上：复制这条链接发到手机打开（手机 QQ 会弹授权），或用手机扫上面的码。',
  'channel.credentialTitle': '连接凭据',
  'channel.saveCredential': '保存凭据并重连',
  'channel.landingDefault': '默认落点',
  'channel.landingSaved': '落点已保存',
  'channel.possibleSame': '可能是同一个群',
  'channel.mergeInto': '合并',
  'channel.merged': '已合并到这个群',
  'channel.configTitle': '通道配置',
  'channel.guideTitle': '开通指引',
  'channel.limitsTitle': '能力与限制',
  'channel.token.title': '对话额度',
  'channel.token.perConversation': '每个对话',
  'channel.token.meter.billable': '按交涉 token 计（未命中输入 + 输出）',
  'channel.token.meter.total': '按全部 token 计（含缓存命中）',
  'channel.token.autoReplyOn': '额度用完自动回一句',
  'channel.token.autoReplyOff': '额度用完静默',
  'channel.token.hintFromPlugin': '默认口径与额度在上面插件的设置里改；改完重载通道生效。',
  'channel.token.empty': '还没有对话用过额度——QQ 侧有人说话后才会出现在这里。',
  'channel.token.blocked': '已用完',
  'channel.token.overridden': '单独设置',
  'channel.token.usedOf': '已用 {used} / {limit}',
  'channel.token.remaining': '剩 {n}',
  'channel.token.adjust': '调整',
  'channel.token.reset': '重置',
  'channel.token.resetAll': '全部重置',
  'channel.token.save': '保存',
  'channel.token.cancel': '取消',
  'channel.token.followDefault': '跟随默认',
  'channel.token.limitPlaceholder': '这个对话的额度（token）',
  'channel.loading': '读取通道状态…',
  'channel.loadFailed': '通道状态读取失败，刷新页面重试',
  'channel.disabled': '管理员未开放',
  'channel.disabledHint': '管理员还没有在控制台开放这条通道，开放后这里就能配置了',
  'channel.statusUnconfigured': '未配置',
  'channel.statusRunning': '运行中',
  'channel.statusStopped': '已停止',
  'channel.instanceHint': '实例 {instance}',
  'channel.step1': '打开 QQ 开放平台，用手机 QQ 扫码登录后创建机器人',
  'channel.step2': '在机器人设置页复制 AppID 与 AppSecret',
  'channel.step3': '填到下面并保存，通道会自动启动',
  'channel.appIdLabel': 'AppID',
  'channel.secretLabel': 'AppSecret',
  'channel.landingTitle': 'QQ 消息落到哪儿',
  'channel.entryHint': '连接到 QQ：填机器人的 AppID / AppSecret，选好消息落到哪个群，保存即生效。',
  'channel.stepTitle': '第一步：拿到 AppID 与 AppSecret',
  'channel.credTitle': '机器人凭据',
  'channel.credHint': '在 QQ 开放平台 → 机器人管理页获取；AppSecret 离开那个页面不保存，就得重新生成。',
  'channel.landingLabel': '落点',
  'channel.landingHint': 'QQ 群里 @ 这个机器人时，消息会落到你选的 Copree 群里，AI 也在那儿回。',
  'channel.allowLabel': '允许的 QQ 群',
  'channel.allowHint': '填群 openid，多个用逗号分隔；留空 = 机器人在的群被 @ 都能进来。',
  'channel.policyTitle': '私聊',
  'channel.recentGroups': '这台机器人的 QQ 群',
  'channel.recentGroupsEmpty': '还没有收到群消息——把机器人拉进 QQ 群并 @ 它一次，群 openid 就会出现在这里。',
  'channel.recentGroupMeta': '{count} 条 · {time}',
  'channel.recentGroupNoMessages': '已指定落点，还没收到消息',
  'channel.recentGroupMembers': '{n} 人',
  'channel.allowAdd': '加入白名单',
  'channel.allowListed': '已在白名单',
  'channel.allowAdded': '已加入白名单',
  'channel.configure': '配置 QQ 通道',
  'channel.policyLabel': '私聊策略',
  'channel.appIdPlaceholder': 'AppID',
  'channel.secretPlaceholder': 'AppSecret（只写不回显）',
  'channel.secretSaved': 'AppSecret 已保存（留空则不改）',
  'channel.policy_pairing': '配对后才能聊',
  'channel.policy_pairingHint': '陌生人私聊只会收到一个配对码，你在这里批准之后才能跟 AI 说话（推荐）',
  'channel.policy_owner': '只认已配对的人',
  'channel.policy_ownerHint': '只有你批准过的人能私聊 AI，其他人静默忽略',
  'channel.policy_open': '谁都能聊',
  'channel.policy_openHint': 'QQ 上任何人都能私聊这个 AI，慎用',
  'channel.policy_off': '关闭私聊',
  'channel.policy_offHint': '私聊一律不处理（群里被 @ 不受影响）',
  'channel.save': '保存并启动',
  'channel.saved': '已保存',
  'channel.start': '启动',
  'channel.started': '已启动',
  'channel.stop': '停止',
  'channel.stopped': '已停止',
  'channel.missing': '还缺：{fields}',
  'channel.lastError': '通道报错：{error}',
  'channel.pairTitle': '配对',
  'channel.pairHint': '让他在 QQ 上私聊一次这个机器人，机器人会回一个配对码；你在这里批准，他才能真正跟 AI 说话。',
  'channel.pendingEmpty': '暂无待批准的申请',
  'channel.unknownUser': 'QQ 用户',
  'channel.codeLabel': '配对码 {code} · 标识 {origin}',
  'channel.approve': '批准',
  'channel.approved': '已批准',
  'channel.block': '拉黑',
  'channel.blocked': '已拉黑',
  'channel.byCodePlaceholder': '直接填配对码',
  'channel.byCode': '按码批准',
  'channel.ownerRow': '已配对：{name}',
  'channel.blockedRow': '已拉黑：{name}',
  'channel.forget': '解除',
  'channel.forgetConfirm': '解除与「{name}」的配对？对方会重新变成陌生人，下次私聊要重新领码。',
  'channel.forgotten': '已解除',
  'channel.unblock': '解除拉黑',
  'channel.privacy': '外部通道只给一个通道侧标识（官方 QQ 是 openid，NapCat 是 QQ 号）：认人靠这个标识加昵称，QQ 号只能当显示名。',
  'channel.groupNone': '只做私聊（不接群）',
  'channel.groupModeExisting': '接回现有群',
  'channel.groupModeNew': '新建一个群',
  'channel.newGroupPlaceholder': '新群名字（留空自动取）',
  'channel.newGroupHint': '保存时会在 Copree 建一个新群（你是群主、这个 AI 是成员），QQ 群里的消息就落在那儿。',
  'channel.groupHint': '选一个 Copree 群：QQ 群里 @ 这个机器人时，消息会落进那个群，AI 也在那里回；不选就只做私聊。',
  'channel.groupEmptyHint': '还没有可选的群：你管理的群、并且这个 AI 已经在里面的群才会出现在这里——先在 Copree 里建个群并把 AI 拉进去。',
  'channel.qqAllowPlaceholder': 'QQ 群白名单（可留空）',
  'channel.qqPullHint': '把机器人拉进 QQ 群这一步要在 QQ 里做：群设置 → 群机器人 → 添加机器人（或用机器人的分享卡片），我们这边替不了。',
  'channel.switchOn': '开启',
  'channel.selfTest': '通道自测',
  'channel.selfTestHint': '在真实出口上发一条测试消息（发给最近来消息的那个会话，只在被动回复窗口内有效）。去 QQ 里看那条：「内联@」后面出现蓝色的你 = 支持内联 @；显示成原文尖括号 = 不支持。',
  'channel.selfTestSent': '已发出（{mode}），去 QQ 里看那条自测消息',
  'channel.selfTestModePassive': '被动回复',
  'channel.selfTestModeActive': '主动消息',
  'channel.selfTestFailed': '自测没发出去',
  // 插件页（表格排版）：搜索/筛选/状态列/详情抽屉
  'plugin.search': '搜索插件',
  'plugin.filterAll': '全部',
  'plugin.count': '共 {n} 个',
  'plugin.rescan': '重扫目录',
  'plugin.detail': '详情',
  'plugin.retry': '重试',
  'ui.collapse': '收起',
  'plugin.close': '关闭',
  'plugin.loading': '加载中…',
  'plugin.empty': '暂无插件',
  'plugin.emptyFiltered': '换个关键词或分类试试',
  'plugin.loadFailed': '插件加载失败',
  'plugin.colName': '插件',
  'plugin.colType': '类型',
  'plugin.colDesc': '说明',
  'plugin.colStatus': '状态',
  'plugin.colAction': '操作',
  'plugin.metaType': '类型',
  'plugin.metaVersion': '版本',
  'plugin.metaAuthor': '作者',
  'plugin.installedFromDisk': '目录安装',
  'plugin.builtin': '内置',
  'plugin.globalOn': '开放',
  'plugin.globalOff': '已关闭',
  'plugin.statusRunning': '{running}/{total} 运行中',
  'plugin.statusStoppedAll': '{total} 个实例均未运行',
  'plugin.statusRunning1': '运行中',
  'plugin.statusStopped': '已停止',
  'plugin.statusNotInstalled': '未安装',
  'plugin.statusBuiltin': '内置',
  'plugin.statusNoInstance': '待添加实例',
  'plugin.statusNeedConfig': '待配置',
  'plugin.usersUsing': '{n} 人在用',
  'plugin.dirHint': '这个插件的开关是全局的：关闭后所有人不可用。用户可在自己的设置里单独启用/停用。',
  'plugin.serviceHint': '实例 = 这个插件的一份配置（例如每个 QQ 机器人一份），可各自启停。',
  'plugin.testBrowser': '测通百度',
  'plugin.capability.dsh': 'DSH 桥接',
  'plugin.capability.dshDesc': '把 DSH 的会话接到 Copree 管理端：在这里给 DSH 发消息、看它的工具流',
  'plugin.capability.docExport': '文档导出',
  'plugin.capability.docExportDesc': '把接口文档导出为 Word/PDF（依赖服务器上的 pandoc）',
  'plugin.capability.apiDoc': '接口文档分区',
  'plugin.capability.apiDocDesc': '管理接口文档的分区标题与介绍文案',
  // 插件协议 v3 多实例：一个插件多份配置（每个机器人一份）
  'pluginService.instances': '实例',
  'pluginService.addInstance': '添加实例',
  'pluginService.instanceNameHint': '实例名（字母/数字/_-.，如 bot-a）',
  'pluginService.instanceExists': '实例名已存在',
  'pluginService.defaultInstance': '默认实例',
  'pluginService.remove': '删除',
  'pluginService.unsaved': '有未保存的改动',
  'pluginService.noInstance': '还没有实例，先添加一个',
  'pluginService.singleHint': '单实例插件：只有一份配置',
  'pluginService.secretSaved': '已保存',
  'pluginService.clear': '清除',
  // 插件协议 v3：service 类插件（运行态 + schema 驱动的配置表单）
  'pluginService.running': '运行中',
  'pluginService.stopped': '已停止',
  'pluginService.start': '启动',
  'pluginService.stop': '停止',
  'pluginService.config': '配置',
  'pluginService.missing': '待配置',
  'pluginService.save': '保存',
  'pluginService.cancel': '取消',
  'pluginService.saved': '配置已保存',
  'pluginService.loadFailed': '读取配置失败',
  'pluginService.saveFailed': '保存失败',
  'pluginService.secretKeep': '已保存，留空则不变',
  'pluginService.blankKeeps': '留空的项不会被修改',
  'pluginService.noConfig': '该插件没有可配置项',
  'toolName.file_read': '读取文件',
  'toolName.file_edit': '编辑文件',
  'toolName.file_write': '写入文件',
  'toolName.file_grep': '搜索文件',
  'toolName.file_list': '列目录',
  'toolName.run_world_code': '运行代码',
  'toolName.web_search': '搜索网页',
  'toolName.web_fetch': '抓取网页',
  'toolName.web_download': '下载资源',
  'toolName.manage_records': '读写记忆',
  'toolName.update_world_info': '更新世界信息',
  'toolName.compact_context': '压缩上下文',
  'toolName.view_api_doc': '查看接口文档',
  'toolName.apply_world_block': '应用积木',
  'toolName.list_world_blocks': '浏览积木',
  'toolName.view_world_block': '查看积木',
  'toolName.suggest_questions': '生成建议',
  'toolName.send_group_message': '发群消息',
  'toolName.get_group_messages': '读群消息',
  'toolName.list_group_members': '列群成员',
  'toolName.get_group_types': '查群类型',
  'toolName.update_group_types': '配群类型',
  // 状态模板（{name}/{summary} 插值）
  'tool.running': '正在{name}…',
  'tool.update': '{summary}',
  'tool.done': '{summary}',
  'world.reasoning': '思考',
  'toolName.file_move': '移动文件',
  'toolName.file_copy': '复制文件',
  'toolName.ask_user': '询问用户',
  'toolName.present_plan': '提交计划',
  'world.approval.title': '需要你确认',
  'world.approval.approve': '同意',
  'world.approval.deny': '不同意',
  'world.approval.waiting': 'AI 正等你选择，选完自动继续',
  'world.approval.remaining': '剩余 {time}',
  'world.approval.notePlaceholder': '说明理由或补充要求（可选，会一起交给 AI）',
  'world.approval.partialButton': '只做其中几条（共 {n} 条）',
  'world.approval.partialConfirm': '只做勾选的 {n} 条',
  'world.approval.partialCancel': '取消',
  'world.approval.selectAll': '全选（共 {n} 条）',
  'world.session.rename': '改名',
  'world.session.renameTitle': '给这场对话改名',
  'world.session.renamePlaceholder': '例如：造卡牌对战界面',
  'world.session.renameHint': '留空 = 清除名字，列表里回落到会话编号',
  'world.session.export': '下载',
  'world.session.exportMd': '下载为 Markdown（人看的）',
  'world.session.exportJson': '下载为 JSON（备份用）',
  'world.session.renameFailed': '改名失败：',
  'world.session.exportFailed': '下载失败：',
  'world.kind.download': '下载',
  'world.kind.delete': '删除',
  'world.kind.modify': '改世界',
  'world.kind.plan': '计划',
  'world.kind.budget': '读取预算',
  'world.kind.other': '其他',
  'dsh.title': 'DSH（DeepSeek Harness）',
  'dsh.desc': '把 Copree 管理端接到 DSH 本体会话：对话、上下文、工具与改代码都发生在 DSH，Copree 只做桥接。',
  'dsh.open': '打开对话',
  'dsh.page.title': 'DSH 对话',
  'dsh.page.back': '返回管理',
  'dsh.page.pick': '选择 DSH 会话，或直接发消息开新对话',
  'dsh.page.hint': '会话、上下文与工具调用都在 DSH 本体，Copree 只做桥接',
  'dsh.page.empty': '此会话暂无内容',
  'dsh.page.loading': '正在载入会话…',
  'dsh.close': '收起',
  'dsh.refresh': '刷新状态',
  'dsh.state.online': '已连接',
  'dsh.state.unreachable': '注册了但连不上',
  'dsh.state.offline': '未检测到',
  'dsh.consentHint': '（需先在 DSH 的「设置 → Copree」里点同意接入，本机才会发心跳；这里的断开只影响 Copree 侧）',
  'dsh.limited': '这里只保留核心功能（对话 / 插队 / 审批 / 提问 / 图片）；其余请在 DSH 网页里操作。',
  'dsh.disconnect': '断开本次连接',
  'dsh.sessions': 'DSH 会话',
  'dsh.newSession': '新对话',
  'dsh.noSessions': '暂无会话',
  'dsh.emptyChat': '此会话暂无内容',
  'dsh.pickHint': '选择左侧会话，或直接发一条消息开新对话',
  'dsh.input': '发给 DSH（Enter 发送，Shift+Enter 换行）',
  'dsh.steer': '插队发送',
  'dsh.send.queue': '排队发送',
  'dsh.busySend.title': '忙时怎么发',
  'dsh.busySend.queue': '排队',
  'dsh.busySend.steer': '插队',
  'dsh.busySend.queueHint': '等这一轮跑完再发（当作下一轮）；Ctrl/⌘+Enter 反过来',
  'dsh.busySend.steerHint': '插到当前一轮的步骤边界，AI 当场看到；Ctrl/⌘+Enter 反过来',
  'dsh.kind.read': '读取',
  'dsh.kind.edit': '编辑',
  'dsh.kind.delete': '删除',
  'dsh.kind.move': '移动',
  'dsh.kind.search': '搜索',
  'dsh.kind.execute': '执行',
  'dsh.kind.fetch': '抓取',
  'dsh.kind.other': '工具',
  'dsh.kind.think': '思考',
  'dsh.ask.title': 'DSH 在等你回答',
  'dsh.ask.submit': '提交回答',
  'dsh.ask.otherPlaceholder': '也可以直接填写（其他答案）',
  'dsh.ask.emptyAnswer': '请先选择一个选项，或填写答案',
  'dsh.ask.multi': '可多选',
  'dsh.send': '发送',
  'dsh.stop': '停止',
  'dsh.ago.now': '刚刚',
  'dsh.ago.minutes': '{n} 分钟前',
  'dsh.ago.hours': '{n} 小时前',
  'dsh.ago.days': '{n} 天前',
  'dsh.msg.me': '我',
  'dsh.msg.sending': '已发出，等 DSH 回显',
  'dsh.msg.cmd': '命令',
  'dsh.msg.cmdRunning': '执行中…',
  'dsh.think.expand': '展开看全文',
  'dsh.think.collapse': '收起',
  'dsh.msg.ai': 'DSH',
  'dsh.msg.thinking': '思考中…',
  'dsh.colWidth': '拖动调整对话列宽',
  'mermaid.renderFailed': 'Mermaid 图表渲染失败：{error}\n\n原始代码：\n```mermaid\n{code}\n```',
  'dsh.scrollBottom': '回到底部',
  'dsh.reconnect': '连接已断开，正在重连',
  'dsh.input.attach': '添加图片',
  'dsh.input.busy': 'DSH 正在处理这一轮…',
  'dsh.input.busyHint': 'DSH 正在处理，输出会实时出现在上面',
  'dsh.attach.drop': '拖动到此处上传图片',
  'dsh.attach.unsupported': '当前 DSH 插件（v0.1）只收到图片文件名；升级插件到 v0.2 并重启 DSH 后，图片本体才会进会话。',
  'dsh.cmd.button': '命令',
  'dsh.cmd.empty': '这条会话没有可用命令',
  'dsh.cmd.unavailable': 'DSH 侧还没有命令服务：更新 dsh-copree 插件并重启 DSH 后可用',
  'dsh.cmd.needsSession': '先选一条会话——命令是在会话里执行的',
  'dsh.cmd.failed': '命令执行失败',
  'dsh.cmd.done': '命令已执行',
  'dsh.cmd.args': '这条命令要填参数，已放进输入框',
  'world.mode.label': '运行模式',
  'world.mode.auto': '自动',
  'world.mode.review': '审阅',
  'world.mode.plan': '计划',
  'world.mode.hint.auto': '不打断：AI 自行下载文件、改动世界机制、删除文件',
  'world.mode.hint.review': '下载 / 删除 / 改动世界机制会弹窗请你确认（同类操作本轮只问一次）',
  'world.mode.hint.plan': 'AI 先探索并提交计划，你通过后才按自动模式执行',
  'world.suggest.title': '发送这条建议？',
  'world.suggest.confirm': '发送',
  'world.suggest.modifyHint': '如需修改：点右边的 ＋ 把它插入输入框再改（不会直接发出去）。',
  // 布局骨架（左栏页签/折叠、顶部收口、输入框、文件树引导线）——文案只在这里维护，三语同序
  'world.rail.tab.chat': '会话',
  'world.rail.tab.files': '工作区',
  'world.rail.collapse': '折叠左栏',
  'world.rail.expand': '展开左栏',
  'world.session.new': '新对话',
  'world.session.default': '默认会话',
  'world.session.more': '更多操作',
  'world.session.pin': '收藏（不被自动清理）',
  'world.session.unpin': '取消收藏',
  'world.session.empty': '只有这一场对话',
  'world.usage.recentTurns': '最近 {n} 轮（越靠右越新）',
  'world.usage.turnTip': '{calls} 次调用 · prompt {prompt} · 命中 {hit}%',
  'world.top.back': '世界列表',
  'world.top.config': '配置',
  'world.top.more': '更多世界操作',
  'world.top.docs': '接口文档',
  'world.top.export': '下载世界包',
  'world.top.import': '导入世界包',
  'world.top.publish': '发布到世界商城',
  'world.top.groups': '入口类型与群助手',
  'world.top.status.active': '活跃',
  'world.top.status.hibernating': '休眠',
  'world.top.flow': '流速 x{rate}',
  'world.chat.focus.on': '专注模式：收起编辑与预览',
  'world.chat.focus.off': '退出专注模式：显示编辑与预览',
  'world.chat.width': '拖动调整对话列宽（两侧留白里也能拖，超出上限时内容居中）',
  'world.queue.title': 'AI 处理中，以下 {n} 条排队（普通消息将插入下一轮 AI 思考，命令等本轮结束执行）',
  'world.queue.image': '（图片）',
  'world.queue.imageCount': '+{n}图',
  'world.queue.kind.cmd': '命令',
  'world.queue.kind.msg': '消息',
  'world.queue.remove': '移除这条',
  'world.preview.open': '预览世界（不用退出专注模式）',
  'world.hint.billing': '世界级会话（非 DM）：账单走世界主人，让它改界面、加功能',
  'world.input.placeholder': '和世界 AI 对话…（输入 / 查看命令；可直接拖图或粘贴截图）',
  'world.input.placeholder.busy': 'AI 处理中，消息将排队…',
  'world.input.attach': '添加图片（拖进面板或 Ctrl+V 粘贴也行）',
  'world.input.send': '发送',
  'world.input.queue': '排队发送',
  'world.input.busyHint': '上一轮还在执行（刷新不影响），完成后自动显示',
  'world.pane.files': '文件',
  'world.pane.preview': '预览',
  'world.pane.immersiveHint': '在沉浸界面新窗口打开（WebView 下自动应用内跳转）',
  'world.pane.previewTitle': '世界预览（{url}）',
  'world.pane.refresh': '刷新',
  'world.pane.immersive': '沉浸窗口',
  'world.pane.render': '渲染',
  'world.pane.edit': '编辑',
  'world.pane.viewSource': '查看原文',
  'world.pane.save': '保存',
  'world.pane.saving': '保存中…',
  'world.pane.noSelection': '未选择文件',
  'world.files.upload': '上传',
  'world.files.new': '新建',
  'world.files.uploadHere': '上传到此位置（{dir}）',
  'world.files.uploadElsewhere': '选择其它位置…',
  'world.files.empty': '空世界，点 + 新建或让机器人生成',
  // 世界 AI 配置弹窗（WorldCreatorConfig）
  'world.creatorConfig.title': '群视界机器人配置',
  'world.creatorConfig.subtitle': '世界的 AI：让它改界面、加功能',
  'world.creatorConfig.close': '关闭',
  'world.creatorConfig.collapse': '收起',
  'world.creatorConfig.usage.cacheHitRate': 'LLM 缓存命中率',
  'world.creatorConfig.usage.summary': '{calls} 次调用 · prompt {prompt} / 缓存 {cached} tok',
  'world.creatorConfig.identity.section': '身份',
  'world.creatorConfig.identity.unnamed': '（未命名）',
  'world.creatorConfig.identity.nameHint': '群视界机器人的名字',
  'world.creatorConfig.identity.editName': '修改名字',
  'world.creatorConfig.identity.namePlaceholder': '如：星野镇的镇守者',
  'world.creatorConfig.modeSwitched': '运行模式已切换为{label}',
  'world.creatorConfig.modeSwitchFailed': '切换运行模式失败: {error}',
  'world.creatorConfig.persona.section': '人设与行为',
  'world.creatorConfig.persona.systemPrompt': '系统提示词（角色人设）',
  'world.creatorConfig.persona.systemPromptUnset': '（未设置，使用默认人设）',
  'world.creatorConfig.persona.editSystemPrompt': '编辑系统提示词',
  'world.creatorConfig.persona.systemPromptPlaceholder': '定义这个世界 AI 的身份、目标和行为准则…',
  'world.creatorConfig.persona.forcedTitle': '平台强注入提示词（不可修改）',
  'world.creatorConfig.persona.forcedDesc': '工具约定 / 能力边界 / 记忆约定 / 运行规范等，每次对话自动附带',
  'world.creatorConfig.persona.expand': '展开查看',
  'world.creatorConfig.persona.forcedEmpty': '（无）',
  'world.creatorConfig.lifecycle.section': '对话生命周期',
  'world.creatorConfig.lifecycle.autoNew': '每日自动开新对话',
  'world.creatorConfig.lifecycle.autoNewDesc': '跨过设定时间自动 /new（旧对话保存可切回）',
  'world.creatorConfig.lifecycle.compact': '空闲自动压缩',
  'world.creatorConfig.lifecycle.compactDesc': '超过 N 小时未对话，下次发消息前自动 compact（0 = 关闭）',
  'world.creatorConfig.lifecycle.retention': '会话保留天数',
  'world.creatorConfig.lifecycle.retentionDesc': '未收藏的会话超过 N 天未活跃将被清理（0 = 不清理）',
  'world.creatorConfig.lifecycle.hours': '小时',
  'world.creatorConfig.lifecycle.days': '天',
  'world.creatorConfig.lifecycle.save': '保存会话设置',
  'world.creatorConfig.model.section': '模型与参数',
  'world.creatorConfig.model.modelLabel': '模型（留空 = 全局默认）',
  'world.creatorConfig.model.modelPlaceholder': '如 deepseek-flash',
  'world.creatorConfig.model.temperature': '温度',
  'world.creatorConfig.model.thinking': '深度思考（推理模式）',
  'world.creatorConfig.model.thinkingDesc': '推理 token 单独计费，费用显著增加',
  'world.creatorConfig.model.maxToolRounds': '工具循环上限',
  'world.creatorConfig.model.maxToolRoundsDesc': '单次对话最多连续调几轮工具（1-200，默认 50）',
  'world.creatorConfig.saving': '保存中...',
  'world.creatorConfig.save': '保存配置',
  'world.creatorConfig.saveSuccess': '世界 AI 配置已保存',
  'world.creatorConfig.saveFailed': '保存失败: {error}',
  'world.creatorConfig.settingsSaved': '会话生命周期设置已保存',
  // 文件编辑器 / 接口文档抽屉 / 世界包弹窗（设计页后半段的文案，原先硬编码在页面里）
  'world.docs.readFailed': '（文档读取失败）',
  'world.docs.exportFailed': 'docx 导出失败: {error}',
  'world.docs.title': '世界 API 接口文档',
  'world.docs.subtitle': '发给世界 AI 的 md（view_api_doc 同源）',
  'world.docs.downloadSection': '下载此分区',
  'world.docs.downloadAll': '下载全部',
  'world.docs.currentSection': '当前分区：{title}',
  'world.docs.allSections': '全部分区合并',
  'world.docs.downloadMd': '下载 .md',
  'world.docs.downloadDocx': '下载 .docx（Word）',
  'world.docs.docxNeedsPandoc': '如需下载为 docx（Word），请前往管理页安装 pandoc 插件后重启后端。',
  'world.top.publishShort': '发布',
  'world.editor.loadFailed': '加载失败: {error}',
  'world.editor.createFailed': '创建失败: {error}',
  'world.editor.saveFailed': '保存失败: {error}',
  'world.editor.deleteFailed': '删除失败: {error}',
  'world.editor.uploadFailed': '上传失败: {error}',
  'world.editor.saved': '已保存',
  'world.editor.uploaded': '已上传',
  'world.editor.deleted': '已删除 {path}',
  'world.editor.confirmDelete': '删除 {path}？',
  'world.editor.newFilePrompt': '新文件名（如 about.html）：',
  'world.editor.uploadPathPrompt': '上传到哪个路径？（当前目录：{dir}）',
  'world.editor.noticeManualEdit': '用户手动编辑了 {file}',
  'world.editor.binaryNotEditable': '（二进制文件，不可编辑）',
  'world.editor.deleteFile': '删除此文件',
  'world.editor.noSubdirs': '当前目录没有子文件夹',
  'world.editor.emptyDirHint': '空目录：点右上角「上传」放文件，或去「会话」让机器人生成',
  'world.editor.uploadDirTitle': '选择上传位置',
  'world.editor.switchToSource': '切到原文/编辑',
  'world.editor.switchToRender': '切到渲染视图',
  'world.zip.downloadedWithData': '已下载世界包（含数据文件）',
  'world.zip.downloadedNoData': '已下载世界包（不含数据文件）',
  'world.zip.downloadFailed': '下载失败: {error}',
  'world.zip.hint': '导出为 zip 压缩包，Windows 可直接解压。',
  'world.zip.full': '完整备份（含数据文件）',
  'world.zip.fullDesc': '代码 + 资源 + 运行数据（content/）',
  'world.zip.codeOnly': '仅代码与资源',
  'world.zip.codeOnlyDesc': '不含运行数据，适合分享给他人',
  'world.import.needZip': '请选择 zip 文件',
  'world.import.done': '导入成功：{n} 个文件',
  'world.import.skipped': '（跳过数据文件 {n}）',
  'world.import.failed': '导入失败: {error}',
  'world.import.hint': '从 zip 导入：已存在的同名文件会被替换，其余文件保持不变。',
  'world.import.keepData': '保留数据文件',
  'world.import.keepDataDesc': '只替换代码与资源，运行数据（content/）不动 · 推荐',
  'world.import.withData': '连同数据文件替换',
  'world.import.withDataDesc': '代码、资源、运行数据全部按包内版本替换',
  'world.creator.nameFallback': '群视界机器人',
  'world.notFound': '世界不存在',
  'world.top.configTitle': '世界 AI 配置（单独表单，不属于 agent）',
  'world.top.docsHint': '接口文档（发给世界 AI 的 md）',
  'world.top.exportHint': '下载世界包（zip）',
  'world.top.importHint': '导入世界包（zip 批量导入，不动数据文件）',
  'world.files.uploadFile': '上传文件',
  // 世界列表页（WorldsPage）与环境绑定弹窗（BindGroupModal）
  'world.list.subtitle': '给群聊一个可编程的世界——游戏、聊天室、小说互动，什么都行',
  'world.list.marketTitle': '世界商城：浏览 / 一键导入别人发布的世界',
  'world.list.market': '商城',
  'world.list.create': '创建世界',
  'world.list.namePlaceholder': '世界名称（如：木头大陆）',
  'world.list.descPlaceholder': '世界观简介（可选）',
  'world.list.createAndDesign': '创建并进入设计页',
  'world.list.empty': '还没有世界',
  'world.list.emptyHint': '创建后绑定群聊或 AI，就能进入沉浸界面了',
  'world.list.entry': '入口:',
  'world.list.unbound': '未绑定',
  'world.list.bindGroup': '绑定群',
  'world.list.bindAgent': '绑定 AI',
  'world.list.wake': '唤醒',
  'world.list.previewTitle': '打开沉浸界面（无需绑定群，只绑定 AI 也能进）',
  'world.list.open': '打开',
  'world.list.design': '设计页',
  'world.list.deleteTitle': '删除世界（含文件与数据，不可恢复）',
  'world.list.confirmDelete': '确定删除世界「{name}」？\n\n会连同它的全部文件、数据、世界 AI 配置一起删除，不可恢复。',
  'world.list.deleted': '世界「{name}」已删除',
  'world.editor.codePlaceholder': '在这里编辑代码…',
  'world.bind.partialFail': '{n} 个群绑定失败',
  'world.bind.partialFailWhy': '{n} 个群绑定失败：{reason}',
  'world.bind.failed': '绑定失败: {error}',
  'world.bind.title': '绑定入口',
  'world.bind.hint': '选类型 → 勾选 → 批量绑定',
  'world.bind.tabGroup': '群聊',
  'world.bind.tabAgent': 'AI',
  'world.bind.step1Group': '1. 选择群聊类型',
  'world.bind.step1Agent': '1. 选择 AI 角色',
  'world.bind.noTypesGroup': '还没有群聊类型——先在「入口类型与群助手」里创建（如 冒险团 / 商会 / 座谈会），才能给群分类。',
  'world.bind.noTypesAgent': '该世界还没定义 AI 角色（如 玩家 / NPC / 裁判）——角色是它在世界里的身份，决定它能拿到哪些技能；旧类型都按群聊类型算，去「入口类型与群助手」把要用的改成 AI 角色，或让群视界机器人添加。',
  'world.bind.unitGroup': '群',
  'world.bind.unitAgent': 'AI',
  'world.bind.step2Group': '2. 勾选群聊（仅显示你群主的群）',
  'world.bind.step2Agent': '2. 勾选 AI（仅显示你创建的 AI）',
  'world.bind.noneGroup': '你没有可绑定的群（需要是你创建的群）。',
  'world.bind.noneAgent': '你没有可绑定的 AI（需要是你创建的 AI）。',
  'world.bind.bound': '已绑定',
  'world.bind.submitEmpty': '绑定',
  'world.bind.submitGroup': '绑定 {n} 个群',
  'world.bind.submitAgent': '绑定 {n} 个 AI',
}

export const toolEn: TranslationDict = {
  // Store (world market / plugin market / installed)
  'store.sectionPlugins': 'Plugin Market',
  'store.sectionInstalled': 'Installed',
  'store.sectionSource': 'Market Source',
  'store.needAdmin': 'Administrator rights required',
  'store.searchPackages': 'Search plugin packages',
  'store.packageCount': '{n} packages',
  'store.uploadPackage': 'Upload Package',
  'store.uploadSuccess': 'Package stored — review the manifest before installing',
  'store.refresh': 'Refresh',
  'store.loading': 'Loading…',
  'store.empty': 'No packages yet',
  'store.emptyHint': 'Packages uploaded by an administrator show up here',
  'store.emptyHintAdmin': 'Upload a zip with plugin.json at its root to get started',
  'store.readonlyHint': 'Install, update and uninstall require administrator rights',
  'store.detail': 'Details',
  'store.close': 'Close',
  'store.install': 'Install',
  'store.upgrade': 'Update',
  'store.uninstall': 'Uninstall',
  'store.installed': 'Installed',
  'store.notInstalled': 'Not installed',
  'store.updatable': 'Update available',
  'store.builtinConflict': 'Same id as a built-in plugin — cannot install',
  'store.brokenPackage': 'Broken package',
  'store.noDescription': '(no description)',
  'store.uncollected': 'unreviewed',
  'store.deletePackage': 'Delete package',
  'store.deleteSuccess': 'Package deleted',
  'store.installSuccess': 'Installed {name}',
  'store.upgradeSuccess': 'Updated {name}',
  'store.uninstallSuccess': 'Uninstalled {name}',
  'store.confirmUninstall': 'Uninstall {name}? Its saved configuration is removed too; built-in plugins are untouched.',
  'store.confirmDeletePackage': 'Delete package {name}? Installed plugins are untouched.',
  'store.fileCount': '{n} files',
  'store.fieldId': 'Plugin id',
  'store.fieldVersion': 'Version',
  'store.fieldAuthor': 'Author',
  'store.fieldCategory': 'Category',
  'store.fieldEntry': 'Entry file',
  'store.fieldSize': 'Unpacked',
  'store.fieldSha': 'SHA-256',
  'store.fieldFiles': 'Files',
  'store.catSkin': 'Skin',
  'store.catSkill': 'Skill',
  'store.catWorld': 'World',
  'store.catService': 'Service',
  'store.catBuiltin': 'Built-in',
  'store.catEmojiPack': 'Emoji pack',
  'store.catOther': 'Other',
  // My AI - channels (QQ): config, pairing, privacy note
  'channel.title': 'QQ Channel',
  'channel.desc': 'Let your AI answer on QQ: group @mentions and direct messages both land in Copree, replies go back to QQ',
  'channel.modalTitle': 'AI channels',
  'channel.hostedTitle': 'Sign in to the protocol endpoint',
  'channel.hostedScan': 'Scan the QR code with mobile QQ to authorise this endpoint (the first sign-in must be confirmed on your phone; the code rotates by itself)',
  'channel.hostedWaiting': 'Waiting for the endpoint to generate a QR code…',
  'channel.hostedLoggedIn': 'Signed in to the endpoint as {name}',
  'channel.hostedLinkHint': 'On a computer: copy this link and open it on your phone (mobile QQ will ask to authorise), or scan the code above.',
  'channel.credentialTitle': 'Credentials',
  'channel.saveCredential': 'Save and reconnect',
  'channel.landingDefault': 'Default group',
  'channel.landingSaved': 'Landing group saved',
  'channel.possibleSame': 'Possibly the same group',
  'channel.mergeInto': 'Merge',
  'channel.merged': 'Merged into that group',
  'channel.configTitle': 'Channel settings',
  'channel.guideTitle': 'Getting started',
  'channel.limitsTitle': 'Capabilities and limits',
  'channel.token.title': 'Conversation budget',
  'channel.token.perConversation': 'Per conversation',
  'channel.token.meter.billable': 'Billable tokens (uncached input + output)',
  'channel.token.meter.total': 'All tokens (including cache hits)',
  'channel.token.autoReplyOn': 'Auto reply when the budget runs out',
  'channel.token.autoReplyOff': 'Silent when the budget runs out',
  'channel.token.hintFromPlugin': 'Defaults are set in the plugin settings above; reload the channel after changing them.',
  'channel.token.empty': 'No conversation has used any budget yet — they appear here once people talk to the bot on QQ.',
  'channel.token.blocked': 'Used up',
  'channel.token.overridden': 'Custom',
  'channel.token.usedOf': 'Used {used} / {limit}',
  'channel.token.remaining': '{n} left',
  'channel.token.adjust': 'Adjust',
  'channel.token.reset': 'Reset',
  'channel.token.resetAll': 'Reset all',
  'channel.token.save': 'Save',
  'channel.token.cancel': 'Cancel',
  'channel.token.followDefault': 'Use default',
  'channel.token.limitPlaceholder': 'Budget for this conversation (tokens)',
  'channel.loading': 'Loading channel status…',
  'channel.loadFailed': 'Could not load channel status, refresh and retry',
  'channel.disabled': 'Not enabled by admin',
  'channel.disabledHint': 'An administrator has not enabled this channel in the console yet',
  'channel.statusUnconfigured': 'Not configured',
  'channel.statusRunning': 'Running',
  'channel.statusStopped': 'Stopped',
  'channel.instanceHint': 'instance {instance}',
  'channel.step1': 'Open the QQ Open Platform and scan the QR code with mobile QQ to create a bot',
  'channel.step2': 'Copy AppID and AppSecret from the bot settings page',
  'channel.step3': 'Paste them below and save — the channel starts automatically',
  'channel.appIdLabel': 'AppID',
  'channel.secretLabel': 'AppSecret',
  'channel.landingTitle': 'Where QQ messages land',
  'channel.entryHint': 'Connect to QQ: fill in the bot AppID / AppSecret, pick where messages land, save and it runs.',
  'channel.stepTitle': 'Step 1: get the AppID and AppSecret',
  'channel.credTitle': 'Bot credentials',
  'channel.credHint': 'From the QQ Open Platform bot settings page; if you leave that page without saving the AppSecret, you must regenerate it.',
  'channel.landingLabel': 'Landing place',
  'channel.landingHint': 'When the bot is @mentioned in a QQ group, the message lands in the Copree group you pick and the AI answers there.',
  'channel.allowLabel': 'Allowed QQ groups',
  'channel.allowHint': 'Group openids, comma separated; empty = any group the bot is in.',
  'channel.policyTitle': 'Direct messages',
  'channel.recentGroups': 'QQ groups of this bot',
  'channel.recentGroupsEmpty': 'No group messages yet — add the bot to a QQ group and @mention it once, and its openid shows up here.',
  'channel.recentGroupMeta': '{count} msg · {time}',
  'channel.recentGroupNoMessages': 'Landing set, no messages yet',
  'channel.recentGroupMembers': '{n} members',
  'channel.allowAdd': 'Allow',
  'channel.allowListed': 'Already allowed',
  'channel.allowAdded': 'Added to allowlist',
  'channel.configure': 'Configure QQ channel',
  'channel.policyLabel': 'Direct message policy',
  'channel.appIdPlaceholder': 'AppID',
  'channel.secretPlaceholder': 'AppSecret (write-only)',
  'channel.secretSaved': 'AppSecret saved (leave empty to keep)',
  'channel.policy_pairing': 'Pair before chatting',
  'channel.policy_pairingHint': 'Strangers only receive a pairing code; you approve them here before they can talk to the AI (recommended)',
  'channel.policy_owner': 'Approved senders only',
  'channel.policy_ownerHint': 'Only people you approved can DM the AI; everyone else is silently ignored',
  'channel.policy_open': 'Anyone can chat',
  'channel.policy_openHint': 'Anyone on QQ can DM this AI — use with care',
  'channel.policy_off': 'Direct messages off',
  'channel.policy_offHint': 'Direct messages are ignored (group @mentions still work)',
  'channel.save': 'Save and start',
  'channel.saved': 'Saved',
  'channel.start': 'Start',
  'channel.started': 'Started',
  'channel.stop': 'Stop',
  'channel.stopped': 'Stopped',
  'channel.missing': 'Missing: {fields}',
  'channel.lastError': 'Channel error: {error}',
  'channel.pairTitle': 'Pairing',
  'channel.pairHint': 'Ask them to DM the bot once on QQ; the bot replies with a pairing code. Approve it here and they can talk to the AI.',
  'channel.pendingEmpty': 'No pending requests',
  'channel.unknownUser': 'QQ user',
  'channel.codeLabel': 'code {code} · ID {origin}',
  'channel.approve': 'Approve',
  'channel.approved': 'Approved',
  'channel.block': 'Block',
  'channel.blocked': 'Blocked',
  'channel.byCodePlaceholder': 'Enter pairing code',
  'channel.byCode': 'Approve by code',
  'channel.ownerRow': 'Paired: {name}',
  'channel.blockedRow': 'Blocked: {name}',
  'channel.forget': 'Unpair',
  'channel.forgetConfirm': 'Unpair {name}? They become a stranger again and must request a new code.',
  'channel.forgotten': 'Unpaired',
  'channel.unblock': 'Unblock',
  'channel.privacy': 'An external channel only exposes a channel-side ID (openid for the official QQ bot, QQ number for NapCat) — identity is that ID plus nickname; a QQ number can only be a display name.',
  'channel.groupNone': 'Direct messages only',
  'channel.groupModeExisting': 'Use an existing group',
  'channel.groupModeNew': 'Create a new group',
  'channel.newGroupPlaceholder': 'New group name (optional)',
  'channel.newGroupHint': 'Saving creates a new Copree group (you own it, the AI is a member) as the landing place for QQ messages.',
  'channel.groupHint': 'Pick a Copree group: when the bot is @mentioned in a QQ group, the message lands in that group and the AI answers there. Leave empty for DM only.',
  'channel.groupEmptyHint': 'No group to pick yet: only groups you manage where this AI is already a member show up here — create one in Copree and add the AI first.',
  'channel.qqAllowPlaceholder': 'QQ group allowlist (optional)',
  'channel.qqPullHint': 'Adding the bot to a QQ group happens on QQ: group settings then group bots then add bot (or the bot share card). We cannot do that step for you.',
  'channel.switchOn': 'On',
  'channel.selfTest': 'Channel self-test',
  'channel.selfTestHint': 'Sends one test message through the real outbound path (to the conversation that last messaged you, and only while the passive-reply window is open). Then look at it on QQ: a blue mention of you after 「内联@」 means inline @ is supported; literal angle brackets mean it is not.',
  'channel.selfTestSent': 'Sent ({mode}) — check that message on QQ',
  'channel.selfTestModePassive': 'passive reply',
  'channel.selfTestModeActive': 'active message',
  'channel.selfTestFailed': 'The self-test could not be sent',
  // Plugin page (table layout): search/filter/status column/detail drawer
  'plugin.search': 'Search plugins',
  'plugin.filterAll': 'All',
  'plugin.count': '{n} total',
  'plugin.rescan': 'Rescan',
  'plugin.detail': 'Details',
  'plugin.retry': 'Retry',
  'ui.collapse': 'Collapse',
  'plugin.close': 'Close',
  'plugin.loading': 'Loading…',
  'plugin.empty': 'No plugins',
  'plugin.emptyFiltered': 'Try another keyword or category',
  'plugin.loadFailed': 'Failed to load plugins',
  'plugin.colName': 'Plugin',
  'plugin.colType': 'Type',
  'plugin.colDesc': 'Description',
  'plugin.colStatus': 'Status',
  'plugin.colAction': 'Actions',
  'plugin.metaType': 'Type',
  'plugin.metaVersion': 'Version',
  'plugin.metaAuthor': 'Author',
  'plugin.installedFromDisk': 'Installed from disk',
  'plugin.builtin': 'built-in',
  'plugin.globalOn': 'Enable',
  'plugin.globalOff': 'Disabled',
  'plugin.statusRunning': '{running}/{total} running',
  'plugin.statusStoppedAll': '{total} instances stopped',
  'plugin.statusRunning1': 'Running',
  'plugin.statusStopped': 'Stopped',
  'plugin.statusNotInstalled': 'Not installed',
  'plugin.statusBuiltin': 'Built-in',
  'plugin.statusNoInstance': 'No instance yet',
  'plugin.statusNeedConfig': 'Needs config',
  'plugin.usersUsing': '{n} using',
  'plugin.dirHint': 'This switch is global: when off, nobody can use it. Each user can still enable or disable it in their own settings.',
  'plugin.serviceHint': 'An instance is one configuration of this plugin (e.g. one QQ bot), started and stopped on its own.',
  'plugin.testBrowser': 'Test browsing',
  'plugin.capability.dsh': 'DSH bridge',
  'plugin.capability.dshDesc': 'Connect a DSH session to the Copree admin: send it prompts and watch its tool stream here',
  'plugin.capability.docExport': 'Document export',
  'plugin.capability.docExportDesc': 'Export API docs to Word/PDF (requires pandoc on the server)',
  'plugin.capability.apiDoc': 'API doc sections',
  'plugin.capability.apiDocDesc': 'Manage section titles and intros of the API documentation',
  // Plugin protocol v3 multi-instance: one plugin, many configurations (one per bot)
  'pluginService.instances': 'Instances',
  'pluginService.addInstance': 'Add instance',
  'pluginService.instanceNameHint': 'Instance name (letters/digits/_-. e.g. bot-a)',
  'pluginService.instanceExists': 'Instance name already exists',
  'pluginService.defaultInstance': 'Default instance',
  'pluginService.remove': 'Remove',
  'pluginService.unsaved': 'Unsaved changes',
  'pluginService.noInstance': 'No instance yet — add one first',
  'pluginService.singleHint': 'Single-instance plugin: one configuration only',
  'pluginService.secretSaved': 'Saved',
  'pluginService.clear': 'Clear',
  // Plugin protocol v3: service plugins (runtime state + schema-driven config form)
  'pluginService.running': 'Running',
  'pluginService.stopped': 'Stopped',
  'pluginService.start': 'Start',
  'pluginService.stop': 'Stop',
  'pluginService.config': 'Configure',
  'pluginService.missing': 'Missing',
  'pluginService.save': 'Save',
  'pluginService.cancel': 'Cancel',
  'pluginService.saved': 'Configuration saved',
  'pluginService.loadFailed': 'Failed to load configuration',
  'pluginService.saveFailed': 'Failed to save',
  'pluginService.secretKeep': 'Saved; leave blank to keep',
  'pluginService.blankKeeps': 'Blank fields are left unchanged',
  'pluginService.noConfig': 'This plugin has no configurable options',
  'toolName.file_read': 'Read file',
  'toolName.file_edit': 'Edit file',
  'toolName.file_write': 'Write file',
  'toolName.file_grep': 'Search file',
  'toolName.file_list': 'List directory',
  'toolName.run_world_code': 'Run code',
  'toolName.web_search': 'Search web',
  'toolName.web_fetch': 'Fetch web',
  'toolName.web_download': 'Download asset',
  'toolName.manage_records': 'Manage memory',
  'toolName.update_world_info': 'Update world info',
  'toolName.compact_context': 'Compact context',
  'toolName.view_api_doc': 'View API docs',
  'toolName.apply_world_block': 'Apply block',
  'toolName.list_world_blocks': 'Browse blocks',
  'toolName.view_world_block': 'View block',
  'toolName.suggest_questions': 'Suggest questions',
  'toolName.send_group_message': 'Send group message',
  'toolName.get_group_messages': 'Read group messages',
  'toolName.list_group_members': 'List group members',
  'toolName.get_group_types': 'Get group types',
  'toolName.update_group_types': 'Configure group types',
  'toolName.file_move': 'Move file',
  'toolName.file_copy': 'Copy file',
  'toolName.ask_user': 'Ask user',
  'toolName.present_plan': 'Present plan',
  'tool.running': 'Running {name}…',
  'tool.update': '{summary}',
  'tool.done': '{summary}',
  'world.reasoning': 'Thinking',
  'world.approval.title': 'Confirmation needed',
  'world.approval.approve': 'Approve',
  'world.approval.deny': 'Decline',
  'world.approval.waiting': 'The AI is waiting for your choice and continues right after',
  'world.approval.remaining': '{time} left',
  'world.approval.notePlaceholder': 'Reason or extra instructions (optional, sent to the AI)',
  'world.approval.partialButton': 'Do only some ({n} items)',
  'world.approval.partialConfirm': 'Do the {n} selected',
  'world.approval.partialCancel': 'Cancel',
  'world.approval.selectAll': 'Select all ({n})',
  'world.session.rename': 'Rename',
  'world.session.renameTitle': 'Rename this conversation',
  'world.session.renamePlaceholder': 'e.g. Card battle UI',
  'world.session.renameHint': 'Leave empty to clear the name and fall back to the session id',
  'world.session.export': 'Export',
  'world.session.exportMd': 'Download as Markdown (for reading)',
  'world.session.exportJson': 'Download as JSON (for backup)',
  'world.session.renameFailed': 'Rename failed: ',
  'world.session.exportFailed': 'Download failed: ',
  'world.kind.download': 'Download',
  'world.kind.delete': 'Delete',
  'world.kind.modify': 'World change',
  'world.kind.plan': 'Plan',
  'world.kind.budget': 'Read budget',
  'world.kind.other': 'Other',
  'dsh.title': 'DSH (DeepSeek Harness)',
  'dsh.desc': 'Bridge the Copree admin console to a DSH-native session: the conversation, context, tools and code edits all happen in DSH; Copree only relays.',
  'dsh.open': 'Open chat',
  'dsh.page.title': 'DSH chat',
  'dsh.page.back': 'Back to admin',
  'dsh.page.pick': 'Pick a DSH session, or just send a message to start one',
  'dsh.page.hint': 'Sessions, context and tool calls all live in DSH itself; Copree only relays',
  'dsh.page.empty': 'This session is empty',
  'dsh.page.loading': 'Loading session…',
  'dsh.close': 'Collapse',
  'dsh.refresh': 'Refresh status',
  'dsh.state.online': 'Connected',
  'dsh.state.unreachable': 'Registered but unreachable',
  'dsh.state.offline': 'Not detected',
  'dsh.consentHint': '(DSH must approve first: Settings → Copree → allow access; the disconnect here only affects Copree)',
  'dsh.limited': 'Only core features are kept here (chat / steer / approvals / questions / images); use the DSH web UI for everything else.',
  'dsh.disconnect': 'Disconnect',
  'dsh.sessions': 'DSH sessions',
  'dsh.newSession': 'New chat',
  'dsh.noSessions': 'No sessions',
  'dsh.emptyChat': 'This session is empty',
  'dsh.pickHint': 'Pick a session on the left, or just send a message to start one',
  'dsh.input': 'Message DSH (Enter to send, Shift+Enter for newline)',
  'dsh.steer': 'Steer now',
  'dsh.send.queue': 'Queue send',
  'dsh.busySend.title': 'How to send while busy',
  'dsh.busySend.queue': 'Queue',
  'dsh.busySend.steer': 'Steer',
  'dsh.busySend.queueHint': 'Send after this turn ends, as the next turn; Ctrl/⌘+Enter does the opposite',
  'dsh.busySend.steerHint': 'Insert at the current step boundary so the agent sees it now; Ctrl/⌘+Enter does the opposite',
  'dsh.kind.read': 'Read',
  'dsh.kind.edit': 'Edit',
  'dsh.kind.delete': 'Delete',
  'dsh.kind.move': 'Move',
  'dsh.kind.search': 'Search',
  'dsh.kind.execute': 'Run',
  'dsh.kind.fetch': 'Fetch',
  'dsh.kind.other': 'Tool',
  'dsh.kind.think': 'Thinking',
  'dsh.ask.title': 'DSH is waiting for your answer',
  'dsh.ask.submit': 'Submit answer',
  'dsh.ask.otherPlaceholder': 'Or type your own answer',
  'dsh.ask.emptyAnswer': 'Pick an option or type an answer first',
  'dsh.ask.multi': 'Multiple selections allowed',
  'dsh.send': 'Send',
  'dsh.stop': 'Stop',
  'dsh.ago.now': 'just now',
  'dsh.ago.minutes': '{n} min ago',
  'dsh.ago.hours': '{n} h ago',
  'dsh.ago.days': '{n} d ago',
  'dsh.msg.me': 'Me',
  'dsh.msg.sending': 'Sent, waiting for DSH to echo',
  'dsh.msg.cmd': 'Command',
  'dsh.msg.cmdRunning': 'Running…',
  'dsh.think.expand': 'Expand',
  'dsh.think.collapse': 'Collapse',
  'dsh.msg.ai': 'DSH',
  'dsh.msg.thinking': 'Thinking…',
  'dsh.colWidth': 'Drag to resize the conversation column',
  'mermaid.renderFailed': 'Mermaid diagram failed to render: {error}\n\nOriginal code:\n```mermaid\n{code}\n```',
  'dsh.scrollBottom': 'Back to bottom',
  'dsh.reconnect': 'Disconnected, reconnecting',
  'dsh.input.attach': 'Add image',
  'dsh.input.busy': 'DSH is working on this turn…',
  'dsh.input.busyHint': 'DSH is working; output streams in above',
  'dsh.attach.drop': 'Drop images here to upload',
  'dsh.attach.unsupported': 'The connected DSH plugin (v0.1) receives image file names only; upgrade the plugin to v0.2 and restart DSH to deliver the images themselves.',
  'dsh.cmd.button': 'Commands',
  'dsh.cmd.empty': 'No commands available for this session',
  'dsh.cmd.unavailable': 'The DSH side has no command service yet: update the dsh-copree plugin and restart DSH',
  'dsh.cmd.needsSession': 'Pick a session first — commands run inside a session',
  'dsh.cmd.failed': 'Command failed',
  'dsh.cmd.done': 'Command executed',
  'dsh.cmd.args': 'This command takes arguments — fill them in the input box',
  'world.mode.label': 'Mode',
  'world.mode.auto': 'Auto',
  'world.mode.review': 'Review',
  'world.mode.plan': 'Plan',
  'world.mode.hint.auto': 'No interruptions: the AI downloads, changes world mechanics and deletes on its own',
  'world.mode.hint.review': 'Downloads / deletions / changes pop up for your approval (once per kind each turn)',
  'world.mode.hint.plan': 'The AI explores and submits a plan first; on approval it runs in auto mode',
  'world.suggest.title': 'Send this suggestion?',
  'world.suggest.confirm': 'Send',
  'world.suggest.modifyHint': 'To edit it instead: click ＋ on the right to insert it into the input box (nothing is sent yet).',
  // Layout skeleton (rail tabs/collapse, compact top bar, composer, tree guides)
  'world.rail.tab.chat': 'Sessions',
  'world.rail.tab.files': 'Workspace',
  'world.rail.collapse': 'Collapse sidebar',
  'world.rail.expand': 'Expand sidebar',
  'world.session.new': 'New chat',
  'world.session.default': 'Default session',
  'world.session.more': 'More actions',
  'world.session.pin': 'Pin (kept from automatic cleanup)',
  'world.session.unpin': 'Unpin',
  'world.session.empty': 'This is the only conversation',
  'world.usage.recentTurns': 'Last {n} turns (newest on the right)',
  'world.usage.turnTip': '{calls} calls · prompt {prompt} · {hit}% cached',
  'world.top.back': 'World list',
  'world.top.config': 'Settings',
  'world.top.more': 'More world actions',
  'world.top.docs': 'API docs',
  'world.top.export': 'Export world package',
  'world.top.import': 'Import world package',
  'world.top.publish': 'Publish to marketplace',
  'world.top.groups': 'Entry types & assistants',
  'world.top.status.active': 'Active',
  'world.top.status.hibernating': 'Dormant',
  'world.top.flow': 'Time flow x{rate}',
  'world.chat.focus.on': 'Focus mode: hide the editor and preview',
  'world.chat.focus.off': 'Exit focus mode: show the editor and preview',
  'world.chat.width': 'Drag to resize the conversation column (grab either side gutter; content stays centered past the cap)',
  'world.queue.title': 'The AI is working — {n} queued (messages join the next turn, commands run after this one)',
  'world.queue.image': '(image)',
  'world.queue.imageCount': '+{n} img',
  'world.queue.kind.cmd': 'Command',
  'world.queue.kind.msg': 'Message',
  'world.queue.remove': 'Remove this item',
  'world.preview.open': 'Preview the world without leaving focus mode',
  'world.hint.billing': 'World-level session (not a DM): usage is billed to the world owner. Ask it to change the UI or add features.',
  'world.input.placeholder': 'Talk to the world AI… (type / for commands; drop or paste images)',
  'world.input.placeholder.busy': 'The AI is working; your message will be queued…',
  'world.input.attach': 'Add an image (drop it into the panel or paste with Ctrl+V)',
  'world.input.send': 'Send',
  'world.input.queue': 'Queue',
  'world.input.busyHint': 'The previous turn is still running (refresh is safe); its output appears when it finishes',
  'world.pane.files': 'Files',
  'world.pane.preview': 'Preview',
  'world.pane.immersiveHint': 'Open in a new window in the immersive UI (jumps in-app inside a WebView)',
  'world.pane.previewTitle': 'World preview ({url})',
  'world.pane.refresh': 'Refresh',
  'world.pane.immersive': 'Immersive window',
  'world.pane.render': 'Render',
  'world.pane.edit': 'Edit',
  'world.pane.viewSource': 'View source',
  'world.pane.save': 'Save',
  'world.pane.saving': 'Saving…',
  'world.pane.noSelection': 'No file selected',
  'world.files.upload': 'Upload',
  'world.files.new': 'New',
  'world.files.uploadHere': 'Upload here ({dir})',
  'world.files.uploadElsewhere': 'Choose another location…',
  'world.files.empty': 'Empty world: click + to create a file, or ask the AI',
  // World AI settings dialog (WorldCreatorConfig)
  'world.creatorConfig.title': 'World bot settings',
  'world.creatorConfig.subtitle': 'The world AI: let it change the UI and add features',
  'world.creatorConfig.close': 'Close',
  'world.creatorConfig.collapse': 'Collapse',
  'world.creatorConfig.usage.cacheHitRate': 'LLM cache hit rate',
  'world.creatorConfig.usage.summary': '{calls} calls · prompt {prompt} / cached {cached} tok',
  'world.creatorConfig.identity.section': 'Identity',
  'world.creatorConfig.identity.unnamed': '(unnamed)',
  'world.creatorConfig.identity.nameHint': 'Name of the world bot',
  'world.creatorConfig.identity.editName': 'Edit name',
  'world.creatorConfig.identity.namePlaceholder': 'e.g. Guardian of Hoshino Town',
  'world.creatorConfig.modeSwitched': 'Mode switched to {label}',
  'world.creatorConfig.modeSwitchFailed': 'Failed to switch mode: {error}',
  'world.creatorConfig.persona.section': 'Persona & behavior',
  'world.creatorConfig.persona.systemPrompt': 'System prompt (persona)',
  'world.creatorConfig.persona.systemPromptUnset': '(not set; the default persona is used)',
  'world.creatorConfig.persona.editSystemPrompt': 'Edit system prompt',
  'world.creatorConfig.persona.systemPromptPlaceholder': 'Define the identity, goals and behavior rules of this world AI…',
  'world.creatorConfig.persona.forcedTitle': 'Platform-injected prompt (read-only)',
  'world.creatorConfig.persona.forcedDesc': 'Tool conventions / capability limits / memory conventions / runtime rules, attached to every turn automatically',
  'world.creatorConfig.persona.expand': 'Expand',
  'world.creatorConfig.persona.forcedEmpty': '(none)',
  'world.creatorConfig.lifecycle.section': 'Conversation lifecycle',
  'world.creatorConfig.lifecycle.autoNew': 'Start a new conversation daily',
  'world.creatorConfig.lifecycle.autoNewDesc': 'Runs /new automatically past the set time (the old conversation is kept and can be switched back)',
  'world.creatorConfig.lifecycle.compact': 'Auto-compact when idle',
  'world.creatorConfig.lifecycle.compactDesc': 'After N hours without messages, compacts before the next message (0 = off)',
  'world.creatorConfig.lifecycle.retention': 'Conversation retention (days)',
  'world.creatorConfig.lifecycle.retentionDesc': 'Unpinned conversations inactive for more than N days are cleaned up (0 = keep)',
  'world.creatorConfig.lifecycle.hours': 'Hours',
  'world.creatorConfig.lifecycle.days': 'Days',
  'world.creatorConfig.lifecycle.save': 'Save conversation settings',
  'world.creatorConfig.model.section': 'Model & parameters',
  'world.creatorConfig.model.modelLabel': 'Model (blank = global default)',
  'world.creatorConfig.model.modelPlaceholder': 'e.g. deepseek-flash',
  'world.creatorConfig.model.temperature': 'Temperature',
  'world.creatorConfig.model.thinking': 'Deep thinking (reasoning mode)',
  'world.creatorConfig.model.thinkingDesc': 'Reasoning tokens are billed separately and cost noticeably more',
  'world.creatorConfig.model.maxToolRounds': 'Max tool rounds',
  'world.creatorConfig.model.maxToolRoundsDesc': 'Max consecutive tool rounds per turn (1-200, default 50)',
  'world.creatorConfig.saving': 'Saving...',
  'world.creatorConfig.save': 'Save settings',
  'world.creatorConfig.saveSuccess': 'World AI settings saved',
  'world.creatorConfig.saveFailed': 'Save failed: {error}',
  'world.creatorConfig.settingsSaved': 'Conversation lifecycle settings saved',
  // File editor / API-docs drawer / world-package dialogs (these used to be hardcoded Chinese)
  'world.docs.readFailed': '(failed to read docs)',
  'world.docs.exportFailed': 'docx export failed: {error}',
  'world.docs.title': 'World API docs',
  'world.docs.subtitle': 'The md handed to the world AI (same source as view_api_doc)',
  'world.docs.downloadSection': 'Download this section',
  'world.docs.downloadAll': 'Download all',
  'world.docs.currentSection': 'Current section: {title}',
  'world.docs.allSections': 'All sections merged',
  'world.docs.downloadMd': 'Download .md',
  'world.docs.downloadDocx': 'Download .docx (Word)',
  'world.docs.docxNeedsPandoc': 'To download as docx (Word), install the pandoc plugin from the admin page and restart the backend.',
  'world.top.publishShort': 'Publish',
  'world.editor.loadFailed': 'Load failed: {error}',
  'world.editor.createFailed': 'Create failed: {error}',
  'world.editor.saveFailed': 'Save failed: {error}',
  'world.editor.deleteFailed': 'Delete failed: {error}',
  'world.editor.uploadFailed': 'Upload failed: {error}',
  'world.editor.saved': 'Saved',
  'world.editor.uploaded': 'Uploaded',
  'world.editor.deleted': 'Deleted {path}',
  'world.editor.confirmDelete': 'Delete {path}?',
  'world.editor.newFilePrompt': 'New file name (e.g. about.html):',
  'world.editor.uploadPathPrompt': 'Upload to which path? (current: {dir})',
  'world.editor.noticeManualEdit': 'You edited {file} by hand',
  'world.editor.binaryNotEditable': '(binary file, not editable)',
  'world.editor.deleteFile': 'Delete this file',
  'world.editor.noSubdirs': 'No subfolders in this directory',
  'world.editor.emptyDirHint': 'Empty directory: upload from the top right, or ask the bot in Sessions',
  'world.editor.uploadDirTitle': 'Choose upload location',
  'world.editor.switchToSource': 'Switch to source / edit',
  'world.editor.switchToRender': 'Switch to rendered view',
  'world.zip.downloadedWithData': 'World package downloaded (with data files)',
  'world.zip.downloadedNoData': 'World package downloaded (code only)',
  'world.zip.downloadFailed': 'Download failed: {error}',
  'world.zip.hint': 'Exported as a zip; Windows can unzip it directly.',
  'world.zip.full': 'Full backup (with data files)',
  'world.zip.fullDesc': 'Code + assets + runtime data (content/)',
  'world.zip.codeOnly': 'Code and assets only',
  'world.zip.codeOnlyDesc': 'No runtime data — good for sharing',
  'world.import.needZip': 'Pick a zip file',
  'world.import.done': 'Imported {n} files',
  'world.import.skipped': ' (skipped {n} data files)',
  'world.import.failed': 'Import failed: {error}',
  'world.import.hint': 'Import from zip: same-named files are replaced, everything else is kept.',
  'world.import.keepData': 'Keep data files',
  'world.import.keepDataDesc': 'Replace code and assets only; runtime data (content/) untouched · recommended',
  'world.import.withData': 'Replace data files too',
  'world.import.withDataDesc': 'Code, assets and runtime data all taken from the package',
  'world.creator.nameFallback': 'World bot',
  'world.notFound': 'World not found',
  'world.top.configTitle': 'World AI config (its own form, not an agent)',
  'world.top.docsHint': 'API docs (the md sent to the world AI)',
  'world.top.exportHint': 'Download world package (zip)',
  'world.top.importHint': 'Import world package (zip, keeps data files)',
  'world.files.uploadFile': 'Upload file',
  // World list page and the binding dialog
  'world.list.subtitle': 'A programmable world for your group chats — games, chat rooms, interactive fiction, anything',
  'world.list.marketTitle': 'World market: browse and one-click import worlds others published',
  'world.list.market': 'Market',
  'world.list.create': 'Create world',
  'world.list.namePlaceholder': 'World name (e.g. Wooden Continent)',
  'world.list.descPlaceholder': 'Short description (optional)',
  'world.list.createAndDesign': 'Create and open designer',
  'world.list.empty': 'No worlds yet',
  'world.list.emptyHint': 'Bind a group chat or an AI after creating one to enter the immersive view',
  'world.list.entry': 'Entry:',
  'world.list.unbound': 'Unbound',
  'world.list.bindGroup': 'Bind group',
  'world.list.bindAgent': 'Bind AI',
  'world.list.wake': 'Wake',
  'world.list.previewTitle': 'Open the immersive view (no group needed — binding an AI is enough)',
  'world.list.open': 'Open',
  'world.list.design': 'Designer',
  'world.list.deleteTitle': 'Delete world (files and data included, irreversible)',
  'world.list.confirmDelete': 'Delete world "{name}"?\n\nAll of its files, data and world AI config go with it. This cannot be undone.',
  'world.list.deleted': 'World "{name}" deleted',
  'world.editor.codePlaceholder': 'Edit code here…',
  'world.bind.partialFail': '{n} groups failed to bind',
  'world.bind.partialFailWhy': '{n} groups failed to bind: {reason}',
  'world.bind.failed': 'Bind failed: {error}',
  'world.bind.title': 'Bind entry',
  'world.bind.hint': 'Pick a type → select → bind in bulk',
  'world.bind.tabGroup': 'Groups',
  'world.bind.tabAgent': 'AI',
  'world.bind.step1Group': '1. Pick a group type',
  'world.bind.step1Agent': '1. Pick an AI role',
  'world.bind.noTypesGroup': 'No group types yet — create one under "Entry types & assistants" (e.g. party / guild / panel) to classify groups.',
  'world.bind.noTypesAgent': 'No AI roles in this world yet (e.g. player / NPC / referee) — a role is what the AI is here, and it decides which skills it gets. Existing types count as group types: switch one to "AI role" under "Entry types & assistants", or ask the world AI.',
  'world.bind.unitGroup': 'groups',
  'world.bind.unitAgent': 'AIs',
  'world.bind.step2Group': '2. Select group chats (only groups you own)',
  'world.bind.step2Agent': '2. Select AIs (only AIs you created)',
  'world.bind.noneGroup': 'No groups available to bind (you must own them).',
  'world.bind.noneAgent': 'No AIs available to bind (you must have created them).',
  'world.bind.bound': 'Bound',
  'world.bind.submitEmpty': 'Bind',
  'world.bind.submitGroup': 'Bind {n} groups',
  'world.bind.submitAgent': 'Bind {n} AIs',
}

export const toolJa: TranslationDict = {
  // マーケット（ワールド / プラグイン / インストール済み）
  'store.sectionPlugins': 'プラグインマーケット',
  'store.sectionInstalled': 'インストール済み',
  'store.sectionSource': 'マーケットソース',
  'store.needAdmin': '管理者権限が必要です',
  'store.searchPackages': 'プラグインパッケージを検索',
  'store.packageCount': '{n} 件のパッケージ',
  'store.uploadPackage': 'パッケージをアップロード',
  'store.uploadSuccess': 'パッケージを保存しました。manifest を確認してからインストールしてください',
  'store.refresh': '更新',
  'store.loading': '読み込み中…',
  'store.empty': 'パッケージがありません',
  'store.emptyHint': '管理者がアップロードするとここに表示されます',
  'store.emptyHintAdmin': 'plugin.json をルートに含む zip をアップロードしてください',
  'store.readonlyHint': 'インストール・更新・アンインストールには管理者権限が必要です',
  'store.detail': '詳細',
  'store.close': '閉じる',
  'store.install': 'インストール',
  'store.upgrade': '更新',
  'store.uninstall': 'アンインストール',
  'store.installed': 'インストール済み',
  'store.notInstalled': '未インストール',
  'store.updatable': '更新あり',
  'store.builtinConflict': '組み込みプラグインと同じ id のためインストールできません',
  'store.brokenPackage': 'パッケージが壊れています',
  'store.noDescription': '（説明なし）',
  'store.uncollected': '未審査',
  'store.deletePackage': 'パッケージを削除',
  'store.deleteSuccess': 'パッケージを削除しました',
  'store.installSuccess': '{name} をインストールしました',
  'store.upgradeSuccess': '{name} を更新しました',
  'store.uninstallSuccess': '{name} をアンインストールしました',
  'store.confirmUninstall': '{name} をアンインストールしますか？保存された設定も削除されます。組み込みプラグインは影響を受けません。',
  'store.confirmDeletePackage': 'パッケージ {name} を削除しますか？インストール済みプラグインは影響を受けません。',
  'store.fileCount': '{n} ファイル',
  'store.fieldId': 'プラグイン id',
  'store.fieldVersion': 'バージョン',
  'store.fieldAuthor': '作者',
  'store.fieldCategory': '種別',
  'store.fieldEntry': 'エントリファイル',
  'store.fieldSize': '展開後',
  'store.fieldSha': 'SHA-256',
  'store.fieldFiles': '含まれるファイル',
  'store.catSkin': 'スキン',
  'store.catSkill': 'スキル',
  'store.catWorld': 'ワールド',
  'store.catService': 'サービス',
  'store.catBuiltin': '組み込み',
  'store.catEmojiPack': '絵文字パック',
  'store.catOther': 'その他',
  // マイAI · チャンネル（QQ）：設定・ペアリング・プライバシー
  'channel.title': 'QQチャンネル',
  'channel.desc': 'AIをQQにつなぐ：グループでの@と個人チャットの両方が Copree に入り、返信はQQへ',
  'channel.modalTitle': 'AIチャンネル',
  'channel.hostedTitle': 'プロトコル端末へのログイン',
  'channel.hostedScan': 'スマホ QQ で QR コードを読み取り、このプロトコル端末を認可してください（初回はスマホ側での確認が必要。コードは自動で更新されます）',
  'channel.hostedWaiting': 'プロトコル端末が QR コードを生成するのを待っています…',
  'channel.hostedLoggedIn': 'プロトコル端末にログイン済み：{name}',
  'channel.hostedLinkHint': 'パソコンでは、このリンクをコピーしてスマホで開く（スマホ QQ が認可を求めます）か、上の QR を読み取ってください。',
  'channel.credentialTitle': '認証情報',
  'channel.saveCredential': '保存して再接続',
  'channel.landingDefault': '既定のグループ',
  'channel.landingSaved': '落点を保存しました',
  'channel.possibleSame': '同じグループの可能性',
  'channel.mergeInto': '統合',
  'channel.merged': 'そのグループに統合しました',
  'channel.configTitle': 'チャンネル設定',
  'channel.guideTitle': '開通ガイド',
  'channel.limitsTitle': 'できること・制限',
  'channel.token.title': '会話ごとの上限',
  'channel.token.perConversation': '会話あたり',
  'channel.token.meter.billable': '課金対象トークン（未キャッシュ入力＋出力）',
  'channel.token.meter.total': '全トークン（キャッシュ命中を含む）',
  'channel.token.autoReplyOn': '上限に達したら自動返信',
  'channel.token.autoReplyOff': '上限に達したら無言',
  'channel.token.hintFromPlugin': '既定の数え方と上限は上のプラグイン設定で変更します。変更後はチャンネルを再読み込みしてください。',
  'channel.token.empty': 'まだ上限を使った会話はありません（QQ 側で誰かが話すとここに表示されます）。',
  'channel.token.blocked': '使い切り',
  'channel.token.overridden': '個別設定',
  'channel.token.usedOf': '使用 {used} / {limit}',
  'channel.token.remaining': '残り {n}',
  'channel.token.adjust': '調整',
  'channel.token.reset': 'リセット',
  'channel.token.resetAll': 'すべてリセット',
  'channel.token.save': '保存',
  'channel.token.cancel': 'キャンセル',
  'channel.token.followDefault': '既定に戻す',
  'channel.token.limitPlaceholder': 'この会話の上限（トークン）',
  'channel.loading': 'チャンネル状態を読み込み中…',
  'channel.loadFailed': 'チャンネル状態を取得できませんでした。再読み込みしてください',
  'channel.disabled': '管理者が未開放',
  'channel.disabledHint': '管理者がコンソールでこのチャンネルを開放すると、ここで設定できます',
  'channel.statusUnconfigured': '未設定',
  'channel.statusRunning': '稼働中',
  'channel.statusStopped': '停止中',
  'channel.instanceHint': 'インスタンス {instance}',
  'channel.step1': 'QQオープンプラットフォームを開き、スマホのQQでQRコードを読み取ってボットを作成',
  'channel.step2': 'ボット設定ページで AppID と AppSecret をコピー',
  'channel.step3': '下に貼り付けて保存すると、チャンネルは自動で起動します',
  'channel.appIdLabel': 'AppID',
  'channel.secretLabel': 'AppSecret',
  'channel.landingTitle': 'QQメッセージの落とし先',
  'channel.entryHint': 'QQに接続：ボットの AppID / AppSecret を入力し、メッセージの落とし先を選んで保存するだけです。',
  'channel.stepTitle': 'ステップ1：AppID と AppSecret を取得',
  'channel.credTitle': 'ボットの認証情報',
  'channel.credHint': 'QQオープンプラットフォームのボット管理ページで取得します。AppSecret はページを離れる前に保存しないと再生成が必要です。',
  'channel.landingLabel': '落とし先',
  'channel.landingHint': 'QQグループでこのボットが@されると、選んだ Copree グループにメッセージが入り、AIもそこで返信します。',
  'channel.allowLabel': '許可するQQグループ',
  'channel.allowHint': 'グループ openid をカンマ区切りで。空欄＝ボットが参加しているグループはすべて許可。',
  'channel.policyTitle': '個人チャット',
  'channel.recentGroups': 'このボットのQQグループ',
  'channel.recentGroupsEmpty': 'グループメッセージはまだありません。ボットをQQグループに追加して一度@すると、openid がここに表示されます。',
  'channel.recentGroupMeta': '{count} 件 · {time}',
  'channel.recentGroupNoMessages': '落点は設定済み、メッセージはまだ',
  'channel.recentGroupMembers': '{n} 人',
  'channel.allowAdd': '許可リストに追加',
  'channel.allowListed': '許可済み',
  'channel.allowAdded': '許可リストに追加しました',
  'channel.configure': 'QQチャンネルを設定',
  'channel.policyLabel': '個人チャットの方針',
  'channel.appIdPlaceholder': 'AppID',
  'channel.secretPlaceholder': 'AppSecret（書き込み専用）',
  'channel.secretSaved': 'AppSecret は保存済み（空欄なら変更しません）',
  'channel.policy_pairing': 'ペアリング後のみ',
  'channel.policy_pairingHint': '見知らぬ人はペアリングコードだけ受け取り、ここで承認するとAIと話せます（推奨）',
  'channel.policy_owner': '承認済みのみ',
  'channel.policy_ownerHint': '承認した人だけがAIにDMできます。それ以外は黙って無視します',
  'channel.policy_open': '誰でも可',
  'channel.policy_openHint': 'QQ上の誰でもこのAIにDMできます。慎重に',
  'channel.policy_off': '個人チャット無効',
  'channel.policy_offHint': '個人チャットは処理しません（グループの@は影響なし）',
  'channel.save': '保存して起動',
  'channel.saved': '保存しました',
  'channel.start': '起動',
  'channel.started': '起動しました',
  'channel.stop': '停止',
  'channel.stopped': '停止しました',
  'channel.missing': '不足：{fields}',
  'channel.lastError': 'チャンネルエラー：{error}',
  'channel.pairTitle': 'ペアリング',
  'channel.pairHint': '相手にQQでボットへ一度DMしてもらうと、ペアリングコードが届きます。ここで承認するとAIと話せます。',
  'channel.pendingEmpty': '承認待ちはありません',
  'channel.unknownUser': 'QQユーザー',
  'channel.codeLabel': 'コード {code} · ID {origin}',
  'channel.approve': '承認',
  'channel.approved': '承認しました',
  'channel.block': 'ブロック',
  'channel.blocked': 'ブロック済み',
  'channel.byCodePlaceholder': 'コードを入力',
  'channel.byCode': 'コードで承認',
  'channel.ownerRow': 'ペアリング済み：{name}',
  'channel.blockedRow': 'ブロック中：{name}',
  'channel.forget': '解除',
  'channel.forgetConfirm': '{name} とのペアリングを解除しますか？相手は再び見知らぬ人になり、次回は新しいコードが必要です。',
  'channel.forgotten': '解除しました',
  'channel.unblock': 'ブロック解除',
  'channel.privacy': '外部チャンネルが渡すのはチャンネル側のIDだけです（公式QQはopenid、NapCatはQQ番号）。本人確認はそのIDとニックネームで行い、QQ番号は表示名にしか使えません。',
  'channel.groupNone': '個人チャットのみ（グループなし）',
  'channel.groupModeExisting': '既存グループを使う',
  'channel.groupModeNew': '新規グループを作る',
  'channel.newGroupPlaceholder': '新しいグループ名（任意）',
  'channel.newGroupHint': '保存時に Copree に新しいグループを作成します（あなたが群主、このAIがメンバー）。QQグループのメッセージはそこに入ります。',
  'channel.groupHint': 'Copree グループを選ぶと、QQグループでこのボットが@されたときメッセージがそのグループに入り、AIもそこで返信します。未選択なら個人チャットのみ。',
  'channel.groupEmptyHint': '選べるグループがありません：あなたが管理し、かつこのAIが既に参加しているグループだけが表示されます。まず Copree でグループを作りAIを追加してください。',
  'channel.qqAllowPlaceholder': 'QQグループ許可リスト（任意）',
  'channel.qqPullHint': 'ボットをQQグループに追加する操作はQQ側です：グループ設定 → グループボット → ボットを追加（または共有カード）。ここからはできません。',
  'channel.switchOn': 'オン',
  'channel.selfTest': 'チャンネル自己テスト',
  'channel.selfTestHint': '実際の送信経路でテストメッセージを1通送ります（最後にメッセージが来た会話あて、受動返信の有効時間内のみ）。QQ側でご確認ください：「内联@」の後ろに青い自分が出ればインライン@対応、山括弧がそのまま出れば未対応です。',
  'channel.selfTestSent': '送信しました（{mode}）。QQ側でそのメッセージをご確認ください',
  'channel.selfTestModePassive': '受動返信',
  'channel.selfTestModeActive': '能動メッセージ',
  'channel.selfTestFailed': '自己テストを送信できませんでした',
  // プラグインページ（表レイアウト）：検索／絞り込み／状態列／詳細ドロワー
  'plugin.search': 'プラグインを検索',
  'plugin.filterAll': 'すべて',
  'plugin.count': '全 {n} 件',
  'plugin.rescan': '再スキャン',
  'plugin.detail': '詳細',
  'plugin.retry': '再試行',
  'ui.collapse': '折りたたむ',
  'plugin.close': '閉じる',
  'plugin.loading': '読み込み中…',
  'plugin.empty': 'プラグインがありません',
  'plugin.emptyFiltered': '別のキーワードや分類で試してください',
  'plugin.loadFailed': 'プラグインの読み込みに失敗',
  'plugin.colName': 'プラグイン',
  'plugin.colType': '種類',
  'plugin.colDesc': '説明',
  'plugin.colStatus': '状態',
  'plugin.colAction': '操作',
  'plugin.metaType': '種類',
  'plugin.metaVersion': 'バージョン',
  'plugin.metaAuthor': '作者',
  'plugin.installedFromDisk': 'ディレクトリから導入',
  'plugin.builtin': '内蔵',
  'plugin.globalOn': '開放',
  'plugin.globalOff': '停止中',
  'plugin.statusRunning': '{running}/{total} 稼働中',
  'plugin.statusStoppedAll': '{total} 件すべて停止',
  'plugin.statusRunning1': '稼働中',
  'plugin.statusStopped': '停止中',
  'plugin.statusNotInstalled': '未インストール',
  'plugin.statusBuiltin': '内蔵',
  'plugin.statusNoInstance': 'インスタンス未追加',
  'plugin.statusNeedConfig': '設定待ち',
  'plugin.usersUsing': '{n} 人が使用中',
  'plugin.dirHint': 'このスイッチは全体設定です。オフにすると誰も使えません。各ユーザーは自分の設定で個別に有効／無効にできます。',
  'plugin.serviceHint': 'インスタンス = このプラグインの 1 つの設定（例：QQ ボットごとに 1 つ）。個別に起動・停止できます。',
  'plugin.testBrowser': 'ブラウザ疎通テスト',
  'plugin.capability.dsh': 'DSH ブリッジ',
  'plugin.capability.dshDesc': 'DSH のセッションを Copree 管理画面に接続：ここから送信し、ツール実行を確認できます',
  'plugin.capability.docExport': 'ドキュメント書き出し',
  'plugin.capability.docExportDesc': 'API ドキュメントを Word/PDF に書き出します（サーバーに pandoc が必要）',
  'plugin.capability.apiDoc': 'API ドキュメント区分',
  'plugin.capability.apiDocDesc': 'API ドキュメントの区分タイトルと紹介文を管理します',
  // プラグイン規約 v3 マルチインスタンス：1 プラグインに複数設定（ボットごとに 1 つ）
  'pluginService.instances': 'インスタンス',
  'pluginService.addInstance': 'インスタンスを追加',
  'pluginService.instanceNameHint': 'インスタンス名（英数字/_-. 例: bot-a）',
  'pluginService.instanceExists': 'インスタンス名が重複しています',
  'pluginService.defaultInstance': '既定のインスタンス',
  'pluginService.remove': '削除',
  'pluginService.unsaved': '未保存の変更があります',
  'pluginService.noInstance': 'インスタンスがありません。先に追加してください',
  'pluginService.singleHint': '単一インスタンスのプラグイン：設定は 1 つだけ',
  'pluginService.secretSaved': '保存済み',
  'pluginService.clear': 'クリア',
  // プラグイン規約 v3：service プラグイン（稼働状態 + schema 駆動の設定フォーム）
  'pluginService.running': '実行中',
  'pluginService.stopped': '停止中',
  'pluginService.start': '起動',
  'pluginService.stop': '停止',
  'pluginService.config': '設定',
  'pluginService.missing': '未設定',
  'pluginService.save': '保存',
  'pluginService.cancel': 'キャンセル',
  'pluginService.saved': '設定を保存しました',
  'pluginService.loadFailed': '設定の読み込みに失敗',
  'pluginService.saveFailed': '保存に失敗',
  'pluginService.secretKeep': '保存済み。空欄なら変更しません',
  'pluginService.blankKeeps': '空欄の項目は変更されません',
  'pluginService.noConfig': 'このプラグインに設定項目はありません',
  'toolName.file_read': 'ファイル読込',
  'toolName.file_edit': 'ファイル編集',
  'toolName.file_write': 'ファイル書込',
  'toolName.file_grep': 'ファイル検索',
  'toolName.file_list': 'ディレクトリ一覧',
  'toolName.run_world_code': 'コード実行',
  'toolName.web_search': 'Web検索',
  'toolName.web_fetch': 'Web取得',
  'toolName.web_download': 'リソース取得',
  'toolName.manage_records': '記憶管理',
  'toolName.update_world_info': '世界情報更新',
  'toolName.compact_context': 'コンテキスト圧縮',
  'toolName.view_api_doc': 'APIドキュメント表示',
  'toolName.apply_world_block': 'ブロック適用',
  'toolName.list_world_blocks': 'ブロック一覧',
  'toolName.view_world_block': 'ブロック表示',
  'toolName.suggest_questions': '提案生成',
  'toolName.send_group_message': 'グループメッセージ送信',
  'toolName.get_group_messages': 'グループメッセージ取得',
  'toolName.list_group_members': 'メンバー一覧',
  'toolName.get_group_types': 'グループタイプ取得',
  'toolName.update_group_types': 'グループタイプ設定',
  'toolName.file_move': 'ファイル移動',
  'toolName.file_copy': 'ファイル複製',
  'toolName.ask_user': 'ユーザーに確認',
  'toolName.present_plan': '計画を提出',
  'tool.running': '{name}を実行中…',
  'tool.update': '{summary}',
  'tool.done': '{summary}',
  'world.reasoning': '思考',
  'world.approval.title': '確認が必要です',
  'world.approval.approve': '同意する',
  'world.approval.deny': '同意しない',
  'world.approval.waiting': 'AI が選択を待っています。選ぶと自動で続行します',
  'world.approval.remaining': '残り {time}',
  'world.approval.notePlaceholder': '理由や追加のご要望（任意・AI にそのまま伝わります）',
  'world.approval.partialButton': '一部だけ実行（全 {n} 件）',
  'world.approval.partialConfirm': '選択した {n} 件だけ実行',
  'world.approval.partialCancel': 'キャンセル',
  'world.approval.selectAll': 'すべて選択（全 {n} 件）',
  'world.session.rename': '名前を変更',
  'world.session.renameTitle': 'この会話の名前を変更',
  'world.session.renamePlaceholder': '例：カードバトル画面',
  'world.session.renameHint': '空欄にすると名前を消し、会話 ID 表示に戻ります',
  'world.session.export': '書き出し',
  'world.session.exportMd': 'Markdown でダウンロード（閲覧用）',
  'world.session.exportJson': 'JSON でダウンロード（バックアップ用）',
  'world.session.renameFailed': '名前の変更に失敗：',
  'world.session.exportFailed': 'ダウンロードに失敗：',
  'world.kind.download': '取得',
  'world.kind.delete': '削除',
  'world.kind.modify': '世界の変更',
  'world.kind.plan': '計画',
  'world.kind.budget': '読み取り予算',
  'world.kind.other': 'その他',
  'dsh.title': 'DSH（DeepSeek Harness）',
  'dsh.desc': 'Copree の管理画面を DSH 本体のセッションに接続します。会話・文脈・ツール・コード編集はすべて DSH 側で行われ、Copree は中継するだけです。',
  'dsh.open': '会話を開く',
  'dsh.page.title': 'DSH チャット',
  'dsh.page.back': '管理に戻る',
  'dsh.page.pick': 'DSH セッションを選択、またはそのまま送信して新規作成',
  'dsh.page.hint': 'セッション・文脈・ツール呼び出しはすべて DSH 本体にあり、Copree は中継のみ',
  'dsh.page.empty': 'このセッションは空です',
  'dsh.page.loading': 'セッションを読み込み中…',
  'dsh.close': '折りたたむ',
  'dsh.refresh': '状態を更新',
  'dsh.state.online': '接続済み',
  'dsh.state.unreachable': '登録済みだが到達不可',
  'dsh.state.offline': '未検出',
  'dsh.consentHint': '（先に DSH の「設定 → Copree」で接続を許可すると心跳が始まります。ここの切断は Copree 側のみ）',
  'dsh.limited': 'ここはコア機能のみ（会話 / 割り込み送信 / 承認 / 質問 / 画像）。それ以外は DSH の Web UI で操作してください。',
  'dsh.disconnect': '接続を切断',
  'dsh.sessions': 'DSH セッション',
  'dsh.newSession': '新しい会話',
  'dsh.noSessions': 'セッションなし',
  'dsh.emptyChat': 'このセッションは空です',
  'dsh.pickHint': '左のセッションを選ぶか、そのままメッセージを送って新規作成',
  'dsh.input': 'DSH へ送信（Enter で送信、Shift+Enter で改行）',
  'dsh.steer': '割り込み送信',
  'dsh.send.queue': '順番に送信',
  'dsh.busySend.title': '実行中の送信方法',
  'dsh.busySend.queue': '順番待ち',
  'dsh.busySend.steer': '割り込み',
  'dsh.busySend.queueHint': 'このターンが終わってから送信します（次のターン扱い）。Ctrl/⌘+Enter は逆になります',
  'dsh.busySend.steerHint': '現在のターンのステップ境界に差し込み、AI がその場で読みます。Ctrl/⌘+Enter は逆になります',
  'dsh.kind.read': '読み取り',
  'dsh.kind.edit': '編集',
  'dsh.kind.delete': '削除',
  'dsh.kind.move': '移動',
  'dsh.kind.search': '検索',
  'dsh.kind.execute': '実行',
  'dsh.kind.fetch': '取得',
  'dsh.kind.other': 'ツール',
  'dsh.kind.think': '思考',
  'dsh.ask.title': 'DSH が回答を待っています',
  'dsh.ask.submit': '回答を送信',
  'dsh.ask.otherPlaceholder': '自由入力もできます（その他の回答）',
  'dsh.ask.emptyAnswer': '先に選択するか、回答を入力してください',
  'dsh.ask.multi': '複数選択可',
  'dsh.send': '送信',
  'dsh.stop': '停止',
  'dsh.ago.now': 'たった今',
  'dsh.ago.minutes': '{n} 分前',
  'dsh.ago.hours': '{n} 時間前',
  'dsh.ago.days': '{n} 日前',
  'dsh.msg.me': '自分',
  'dsh.msg.sending': '送信済み（DSH の反映待ち）',
  'dsh.msg.cmd': 'コマンド',
  'dsh.msg.cmdRunning': '実行中…',
  'dsh.think.expand': '全文を表示',
  'dsh.think.collapse': '折りたたむ',
  'dsh.msg.ai': 'DSH',
  'dsh.msg.thinking': '考え中…',
  'dsh.colWidth': 'ドラッグで会話列の幅を調整',
  'mermaid.renderFailed': 'Mermaid ダイアグラムの描画に失敗しました：{error}\n\n元のコード：\n```mermaid\n{code}\n```',
  'dsh.scrollBottom': '一番下へ',
  'dsh.reconnect': '接続が切れました。再接続中',
  'dsh.input.attach': '画像を追加',
  'dsh.input.busy': 'DSH がこのターンを処理中…',
  'dsh.input.busyHint': 'DSH が処理中です。出力は上に流れます',
  'dsh.attach.drop': 'ここに画像をドロップしてアップロード',
  'dsh.attach.unsupported': '接続中の DSH プラグイン（v0.1）は画像のファイル名のみ受け取ります。プラグインを v0.2 に更新して DSH を再起動すると、画像本体がセッションに届きます。',
  'dsh.cmd.button': 'コマンド',
  'dsh.cmd.empty': 'このセッションで使えるコマンドはありません',
  'dsh.cmd.unavailable': 'DSH 側にコマンドサービスがありません。dsh-copree プラグインを更新して DSH を再起動すると使えます',
  'dsh.cmd.needsSession': '先にセッションを選んでください（コマンドはセッション内で実行されます）',
  'dsh.cmd.failed': 'コマンドの実行に失敗しました',
  'dsh.cmd.done': 'コマンドを実行しました',
  'dsh.cmd.args': '引数が必要なコマンドです。入力欄に記入してください',
  'world.mode.label': '実行モード',
  'world.mode.auto': '自動',
  'world.mode.review': '確認',
  'world.mode.plan': '計画',
  'world.mode.hint.auto': '中断なし：AI が取得・仕組み変更・削除を自ら実行します',
  'world.mode.hint.review': '取得 / 削除 / 仕組み変更は確認ダイアログが出ます（同種はこのターン一度だけ）',
  'world.mode.hint.plan': 'AI が先に計画を提出し、承認後に自動モードで実行します',
  'world.suggest.title': 'この提案を送信しますか？',
  'world.suggest.confirm': '送信',
  'world.suggest.modifyHint': '修正したい場合：右側の ＋ で入力欄に挿入してから編集できます（すぐには送信されません）。',
  // レイアウト骨格（左レールのタブ/折りたたみ、上部の集約、入力欄、ツリーのガイド線）
  'world.rail.tab.chat': 'セッション',
  'world.rail.tab.files': 'ワークスペース',
  'world.rail.collapse': 'サイドバーを折りたたむ',
  'world.rail.expand': 'サイドバーを展開',
  'world.session.new': '新しい会話',
  'world.session.default': '既定のセッション',
  'world.session.more': 'その他の操作',
  'world.session.pin': 'ピン留め（自動整理から除外）',
  'world.session.unpin': 'ピン留めを解除',
  'world.session.empty': '会話はこれだけです',
  'world.usage.recentTurns': '直近 {n} ターン（右が最新）',
  'world.usage.turnTip': '{calls} 回 · prompt {prompt} · ヒット {hit}%',
  'world.top.back': 'ワールド一覧',
  'world.top.config': '設定',
  'world.top.more': 'ワールド操作',
  'world.top.docs': 'API ドキュメント',
  'world.top.export': 'ワールドを書き出す',
  'world.top.import': 'ワールドを取り込む',
  'world.top.publish': 'マーケットに公開',
  'world.top.groups': 'エントリー種別とアシスタント',
  'world.top.status.active': 'アクティブ',
  'world.top.status.hibernating': '休眠',
  'world.top.flow': '時間流速 x{rate}',
  'world.chat.focus.on': '集中モード：編集とプレビューを隠す',
  'world.chat.focus.off': '集中モードを解除：編集とプレビューを表示',
  'world.chat.width': 'ドラッグで会話カラムの幅を調整（左右の余白からも可、上限を超えると内容は中央寄せ）',
  'world.queue.title': 'AI 実行中：{n} 件が待機中（通常メッセージは次のターンに挿入、コマンドは今回の終了後に実行）',
  'world.queue.image': '（画像）',
  'world.queue.imageCount': '+{n} 画像',
  'world.queue.kind.cmd': 'コマンド',
  'world.queue.kind.msg': 'メッセージ',
  'world.queue.remove': 'この項目を削除',
  'world.preview.open': '集中モードのままワールドをプレビュー',
  'world.hint.billing': 'ワールド級セッション（DM ではありません）：課金はワールド所有者に帰属します。UI の変更や機能追加を依頼できます。',
  'world.input.placeholder': 'ワールド AI と会話…（/ でコマンド、画像はドロップまたは貼り付け）',
  'world.input.placeholder.busy': 'AI 実行中です。メッセージはキューに追加されます…',
  'world.input.attach': '画像を追加（パネルにドロップ、または Ctrl+V で貼り付け）',
  'world.input.send': '送信',
  'world.input.queue': 'キューに追加',
  'world.input.busyHint': '前のターンがまだ実行中です（更新しても安全）。完了すると自動で表示されます',
  'world.pane.files': 'ファイル',
  'world.pane.preview': 'プレビュー',
  'world.pane.immersiveHint': 'イマーシブUIの新規ウィンドウで開く（WebView ではアプリ内遷移）',
  'world.pane.previewTitle': 'ワールドプレビュー（{url}）',
  'world.pane.refresh': '更新',
  'world.pane.immersive': 'イマーシブウィンドウ',
  'world.pane.render': 'レンダリング',
  'world.pane.edit': '編集',
  'world.pane.viewSource': '原文を表示',
  'world.pane.save': '保存',
  'world.pane.saving': '保存中…',
  'world.pane.noSelection': 'ファイル未選択',
  'world.files.upload': 'アップロード',
  'world.files.new': '新規',
  'world.files.uploadHere': 'ここにアップロード（{dir}）',
  'world.files.uploadElsewhere': '別の場所を選ぶ…',
  'world.files.empty': '空のワールドです。＋ で作成するか AI に依頼してください',
  // ワールド AI 設定ダイアログ（WorldCreatorConfig）
  'world.creatorConfig.title': 'ワールドボット設定',
  'world.creatorConfig.subtitle': 'ワールドの AI：UI の変更や機能追加を依頼できます',
  'world.creatorConfig.close': '閉じる',
  'world.creatorConfig.collapse': '折りたたむ',
  'world.creatorConfig.usage.cacheHitRate': 'LLM キャッシュヒット率',
  'world.creatorConfig.usage.summary': '{calls} 回 · prompt {prompt} / キャッシュ {cached} tok',
  'world.creatorConfig.identity.section': 'アイデンティティ',
  'world.creatorConfig.identity.unnamed': '（名前未設定）',
  'world.creatorConfig.identity.nameHint': 'ワールドボットの名前',
  'world.creatorConfig.identity.editName': '名前を変更',
  'world.creatorConfig.identity.namePlaceholder': '例：星野町の守り手',
  'world.creatorConfig.modeSwitched': '実行モードを {label} に切り替えました',
  'world.creatorConfig.modeSwitchFailed': '実行モードの切り替えに失敗：{error}',
  'world.creatorConfig.persona.section': '人格と挙動',
  'world.creatorConfig.persona.systemPrompt': 'システムプロンプト（人格）',
  'world.creatorConfig.persona.systemPromptUnset': '（未設定・既定の人格を使用）',
  'world.creatorConfig.persona.editSystemPrompt': 'システムプロンプトを編集',
  'world.creatorConfig.persona.systemPromptPlaceholder': 'このワールド AI の立場・目的・行動規範を定義…',
  'world.creatorConfig.persona.forcedTitle': 'プラットフォーム注入プロンプト（変更不可）',
  'world.creatorConfig.persona.forcedDesc': 'ツール規約 / 能力の範囲 / 記憶の規約 / 実行規範など、毎回の会話に自動で付与されます',
  'world.creatorConfig.persona.expand': '展開して表示',
  'world.creatorConfig.persona.forcedEmpty': '（なし）',
  'world.creatorConfig.lifecycle.section': '会話ライフサイクル',
  'world.creatorConfig.lifecycle.autoNew': '毎日自動で新しい会話を開始',
  'world.creatorConfig.lifecycle.autoNewDesc': '設定時刻を過ぎると自動で /new（旧会話は保存され、いつでも戻せます）',
  'world.creatorConfig.lifecycle.compact': 'アイドル時に自動圧縮',
  'world.creatorConfig.lifecycle.compactDesc': 'N 時間会話がないと、次の送信前に自動 compact（0 = 無効）',
  'world.creatorConfig.lifecycle.retention': '会話の保持日数',
  'world.creatorConfig.lifecycle.retentionDesc': 'ピン留めしていない会話は N 日間未使用で削除されます（0 = 削除しない）',
  'world.creatorConfig.lifecycle.hours': '時間',
  'world.creatorConfig.lifecycle.days': '日',
  'world.creatorConfig.lifecycle.save': '会話設定を保存',
  'world.creatorConfig.model.section': 'モデルとパラメータ',
  'world.creatorConfig.model.modelLabel': 'モデル（空欄 = 全体の既定）',
  'world.creatorConfig.model.modelPlaceholder': '例：deepseek-flash',
  'world.creatorConfig.model.temperature': '温度',
  'world.creatorConfig.model.thinking': '深い思考（推論モード）',
  'world.creatorConfig.model.thinkingDesc': '推論トークンは別途課金され、費用が大きく増えます',
  'world.creatorConfig.model.maxToolRounds': 'ツールループ上限',
  'world.creatorConfig.model.maxToolRoundsDesc': '1 回の会話で連続してツールを呼ぶ上限（1〜200、既定 50）',
  'world.creatorConfig.saving': '保存中...',
  'world.creatorConfig.save': '設定を保存',
  'world.creatorConfig.saveSuccess': 'ワールド AI 設定を保存しました',
  'world.creatorConfig.saveFailed': '保存に失敗：{error}',
  'world.creatorConfig.settingsSaved': '会話ライフサイクル設定を保存しました',
  // ファイルエディタ / API ドキュメントドロワー / ワールドパッケージのダイアログ（従来は中国語直書き）
  'world.docs.readFailed': '（ドキュメントの読み込みに失敗）',
  'world.docs.exportFailed': 'docx 出力に失敗: {error}',
  'world.docs.title': 'ワールド API ドキュメント',
  'world.docs.subtitle': 'ワールド AI に渡す md（view_api_doc と同源）',
  'world.docs.downloadSection': 'このセクションをダウンロード',
  'world.docs.downloadAll': 'すべてダウンロード',
  'world.docs.currentSection': '現在のセクション：{title}',
  'world.docs.allSections': '全セクションを結合',
  'world.docs.downloadMd': '.md をダウンロード',
  'world.docs.downloadDocx': '.docx（Word）をダウンロード',
  'world.docs.docxNeedsPandoc': 'docx（Word）でダウンロードするには、管理ページで pandoc プラグインをインストールしてバックエンドを再起動してください。',
  'world.top.publishShort': '公開',
  'world.editor.loadFailed': '読み込みに失敗: {error}',
  'world.editor.createFailed': '作成に失敗: {error}',
  'world.editor.saveFailed': '保存に失敗: {error}',
  'world.editor.deleteFailed': '削除に失敗: {error}',
  'world.editor.uploadFailed': 'アップロードに失敗: {error}',
  'world.editor.saved': '保存しました',
  'world.editor.uploaded': 'アップロードしました',
  'world.editor.deleted': '{path} を削除しました',
  'world.editor.confirmDelete': '{path} を削除しますか？',
  'world.editor.newFilePrompt': '新しいファイル名（例：about.html）：',
  'world.editor.uploadPathPrompt': 'どのパスにアップロードしますか？（現在：{dir}）',
  'world.editor.noticeManualEdit': '{file} を手動で編集しました',
  'world.editor.binaryNotEditable': '（バイナリファイルのため編集不可）',
  'world.editor.deleteFile': 'このファイルを削除',
  'world.editor.noSubdirs': 'このディレクトリにサブフォルダはありません',
  'world.editor.emptyDirHint': '空のディレクトリ：右上からアップロードするか、「セッション」でボットに依頼してください',
  'world.editor.uploadDirTitle': 'アップロード先を選択',
  'world.editor.switchToSource': '原文／編集に切替',
  'world.editor.switchToRender': 'レンダリング表示に切替',
  'world.zip.downloadedWithData': 'ワールドパッケージをダウンロードしました（データファイル含む）',
  'world.zip.downloadedNoData': 'ワールドパッケージをダウンロードしました（コードのみ）',
  'world.zip.downloadFailed': 'ダウンロードに失敗: {error}',
  'world.zip.hint': 'zip として書き出します。Windows でもそのまま解凍できます。',
  'world.zip.full': '完全バックアップ（データファイル含む）',
  'world.zip.fullDesc': 'コード + リソース + 実行データ（content/）',
  'world.zip.codeOnly': 'コードとリソースのみ',
  'world.zip.codeOnlyDesc': '実行データを含まないため共有に適しています',
  'world.import.needZip': 'zip ファイルを選択してください',
  'world.import.done': '{n} 個のファイルをインポートしました',
  'world.import.skipped': '（データファイル {n} 個をスキップ）',
  'world.import.failed': 'インポートに失敗: {error}',
  'world.import.hint': 'zip からインポート：同名ファイルは置き換え、それ以外はそのまま保持します。',
  'world.import.keepData': 'データファイルを保持',
  'world.import.keepDataDesc': 'コードとリソースのみ置き換え、実行データ（content/）はそのまま · 推奨',
  'world.import.withData': 'データファイルも置き換え',
  'world.import.withDataDesc': 'コード・リソース・実行データをすべてパッケージ側に合わせます',
  'world.creator.nameFallback': 'ワールドボット',
  'world.notFound': 'ワールドが存在しません',
  'world.top.configTitle': 'ワールド AI 設定（agent ではない専用フォーム）',
  'world.top.docsHint': 'API ドキュメント（ワールド AI に渡す md）',
  'world.top.exportHint': 'ワールドパッケージをダウンロード（zip）',
  'world.top.importHint': 'ワールドパッケージをインポート（zip・データファイルは保持）',
  'world.files.uploadFile': 'ファイルをアップロード',
  // ワールド一覧ページと紐づけダイアログ
  'world.list.subtitle': 'グループチャットにプログラマブルな世界を——ゲーム、チャットルーム、小説インタラクション、何でも',
  'world.list.marketTitle': 'ワールドマーケット：公開されたワールドを見る／ワンクリックで取り込む',
  'world.list.market': 'マーケット',
  'world.list.create': 'ワールドを作成',
  'world.list.namePlaceholder': 'ワールド名（例：木の大陸）',
  'world.list.descPlaceholder': '世界観の紹介（任意）',
  'world.list.createAndDesign': '作成して設計ページへ',
  'world.list.empty': 'まだワールドがありません',
  'world.list.emptyHint': '作成後にグループチャットか AI を紐づけると、没入ビューに入れます',
  'world.list.entry': '入口:',
  'world.list.unbound': '未紐づけ',
  'world.list.bindGroup': 'グループを紐づけ',
  'world.list.bindAgent': 'AI を紐づけ',
  'world.list.wake': '起床',
  'world.list.previewTitle': '没入ビューを開く（グループ不要、AI の紐づけだけでも可）',
  'world.list.open': '開く',
  'world.list.design': '設計ページ',
  'world.list.deleteTitle': 'ワールドを削除（ファイルとデータを含む・復元不可）',
  'world.list.confirmDelete': 'ワールド「{name}」を削除しますか？\n\nすべてのファイル・データ・ワールド AI 設定も一緒に削除され、復元できません。',
  'world.list.deleted': 'ワールド「{name}」を削除しました',
  'world.editor.codePlaceholder': 'ここでコードを編集…',
  'world.bind.partialFail': '{n} 件のグループ紐づけに失敗',
  'world.bind.partialFailWhy': '{n} 件のグループ紐づけに失敗：{reason}',
  'world.bind.failed': '紐づけに失敗: {error}',
  'world.bind.title': '入口の紐づけ',
  'world.bind.hint': '種類を選ぶ → チェック → 一括紐づけ',
  'world.bind.tabGroup': 'グループ',
  'world.bind.tabAgent': 'AI',
  'world.bind.step1Group': '1. グループ種別を選択',
  'world.bind.step1Agent': '1. AI ロールを選択',
  'world.bind.noTypesGroup': 'グループ種別がまだありません——「エントリー種別とアシスタント」で作成してください（例：冒険団/商会/座談会）。',
  'world.bind.noTypesAgent': 'この世界にはまだ AI ロールがありません（例：プレイヤー／NPC／審判）——ロールは世界での立場で、使えるスキルが決まります。既存の種別はグループ種別として扱われるため、「エントリー種別とアシスタント」で AI ロールに変更するか、世界 AI に依頼してください。',
  'world.bind.unitGroup': 'グループ',
  'world.bind.unitAgent': 'AI',
  'world.bind.step2Group': '2. グループチャットを選択（あなたがオーナーのグループのみ）',
  'world.bind.step2Agent': '2. AI を選択（あなたが作成した AI のみ）',
  'world.bind.noneGroup': '紐づけ可能なグループがありません（あなたが作成したグループが必要です）。',
  'world.bind.noneAgent': '紐づけ可能な AI がありません（あなたが作成した AI が必要です）。',
  'world.bind.bound': '紐づけ済み',
  'world.bind.submitEmpty': '紐づけ',
  'world.bind.submitGroup': '{n} 件のグループを紐づけ',
  'world.bind.submitAgent': '{n} 件の AI を紐づけ',
}
